/**
 * @file edit-document-content.component.ts
 * @module EditDocumentContentComponent
 *
 * @description
 * Smart Angular component that owns the full lifecycle of a document's line-item
 * grid — loading, enrichment, inline editing, price recalculation, profit analysis,
 * status-flag evaluation, and Excel export.
 *
 * ## Key collaborators
 * | Service | Role |
 * |---------|------|
 * {@link DocumentApiService} | CRUD for document items; batch enrichment endpoint |
 * {@link ProductApiService}  | Price info, stock levels, income prices, ACTC codes |
 * {@link GetglobalsettingsService} | VAT rate, stock-depot IDs (`ms` / `is` / `as`) |
 * {@link KeycloakService}    | Role-based visibility (`canSeeFinanceResult`, etc.) |
 * {@link MessageService}     | PrimeNG toast notifications |
 * {@link FilterService}      | Custom PrimeNG table filter registrations |
 *
 * ## Inputs
 * | Name | Type | Description |
 * |------|------|-------------|
 * | `inputDocument` | `Document` | The document whose items are displayed. Changing this triggers a full reload. |
 * | `setShowRowOptions` | `number[]` | Bitmask controlling which optional columns are visible (overrides localStorage). |
 * | `showHeader` | `boolean` | Whether the component renders its own header bar. |
 * | `allowEditControls` | `boolean` | Enables / disables the editing toolbar. |
 * | `showPaysum` | `boolean` | Shows / hides the payment-sum footer area. |
 * | `countEmpty` | `boolean` | When `true`, zero-qty rows are shown in the table and included in totals and printing; when `false` they are hidden. |
 * | `knockKnock` | `boolean` | Reserved trigger input (currently unused). |
 *
 * ## Outputs
 * | Name | Payload | Description |
 * |------|---------|-------------|
 * | `editing` | `boolean` | `true` while any row is in edit mode or bulk changes are pending. |
 * | `contentLoading` | `boolean` | Mirrors the internal `contentIsLoading` flag for parent spinners. |
 * | `getDocumentStatus` | `number` | A {@link DocumentStatusFlag} value: `0` = OK, `10` = MinorIssue, `100` = SeriousError. |
 * | `countEmptyChange` | `boolean` | Two-way binding partner for `countEmpty`. |
 *
 * ## Observable data-flow
 *
 * ### Full document load
 * ```
 * reloadDocContent() / ngOnChanges(inputDocument)
 *   └─ docItemList$.next(docId)          [debounced 300 ms]
 *       └─ DocumentApiService.getDocItemsByDoc()
 *           └─ calculateDocItemsFieldsObservable(rawItems[])
 *               ├─ DocumentApiService.getDocItemsEnriched()  [single batch HTTP]
 *               ├─ applyEnrichedData()                       [synchronous merge]
 *               ├─ calculateDiRowProfitSync()                [per row, no HTTP]
 *               ├─ calculateDiRowSum()                       [per row, no HTTP]
 *               └─ calculateDiRowStatusObservable()          [per row, no HTTP]
 *           └─ calculateDocumentSummaryObservable()          [debounced 500 ms]
 * ```
 *
 * ### Single-row fast refresh (after save / cancel)
 * ```
 * fastReloadDocContent(di)
 *   └─ pullDiRowPriceAndStockObservable(di)   [4 parallel HTTP calls via forkJoin]
 *       └─ calculateDiRowProfitObservable(di)
 *           ├─ pullDiRowStockIncomePriceObservable(di)  [stock + 3 income-price calls]
 *           └─ calculateDiRowProfitSync(di)             [FIFO weighted average]
 *       └─ calculateDocumentSummaryObservable()
 * ```
 *
 * ### Price-field edit (cell change handler)
 * ```
 * onDOCITEMVPRICEChange / onDOCITEMPRICEChange / onDISCOUNTChange / …
 *   └─ recalculatePrices(di, changedFields[])
 *       ├─ [sync path — price/discount/u4etka]
 *       │   calculateDiRowProfitSync() → calculateDiRowStatusObservable()
 *       │   → calculateDocumentSummaryObservable()
 *       └─ [async path — qty]
 *           calculateDiRowProfitObservable() → calculateDiRowStatusObservable()
 *           → calculateDocumentSummaryObservable()
 * ```
 *
 * ## Error handling
 * All HTTP calls are wrapped with {@link handleError}, which:
 * - increments `errorCount`;
 * - shows a PrimeNG toast with a localised message;
 * - returns a safe fallback (`of(result)` or `EMPTY`).
 *
 * Once `errorCount` reaches `MAX_ERRORS` (5), {@link canProceed} blocks further
 * processing and shows a sticky "reload the page" toast.
 *
 * ## Subscription management
 * - **Short-lived subs** are pushed into `activeSubscriptions[]` and mass-unsubscribed
 *   in `ngOnDestroy`.
 * - **Per-row recalc subs** are tracked in `recalcSubscriptions: Map<DOCITEMID, Subscription>`
 *   so a new qty-change cancels the previous pending call for the same row.
 * - **Long-lived named subs** (`docItemQuery`, `diSaveQuery`, etc.) are replaced via
 *   {@link reassignSubscription} to prevent stacking.
 * - The main `docItemList$` pipeline uses `takeUntil(destroy$)` for automatic teardown.
 *
 * @author  Sergii K.
 * @since   2024
 */
import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation, EventEmitter, OnChanges, SimpleChanges, Input, Output, SimpleChange, HostListener } from '@angular/core';
import { formatNumber } from '@angular/common';
import { forkJoin, toArray, from, of, tap, finalize, Subscription, Subject, Observable, throwError, EMPTY } from 'rxjs';
import { map, switchMap, concatMap, debounceTime, filter, takeUntil, takeLast, catchError, retry, timeout } from 'rxjs/operators';

import { MenuItem, MessageService, FilterService } from 'primeng/api';
import { Table } from 'primeng/table';

import { KeycloakService } from 'keycloak-angular';
import { saveAs } from 'file-saver';

import { environment } from 'src/environments/environment';

import { round1, round2 } from 'src/app/shared/round';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { Document, DocItems, DocumentStatusFlag } from 'src/app/shared/document';
import { Product, ApprovedCustomsCodes } from 'src/app/shared/product';
import { ProductApiService } from 'src/app/services/product-api.service';
import { DocumentApiService } from 'src/app/services/document-api.service';
import { ClientApiService } from 'src/app/services/client-api.service';
import { getTierDiscountPercent } from 'src/app/services/doc-item-pricing.service';
import { AgentCustomerTier } from 'src/app/shared/agent-admin';
import { applyEnrichedData } from 'src/app/shared/doc-items-enricher';
import { IncomePriceBreakdownDialogComponent } from 'src/app/visualcomponents/income-price-breakdown-dialog/income-price-breakdown-dialog.component';

type PriceField = 'price' | 'vprice' | 'priceNoVat' | 'discount' | 'u4etka' | 'qty';

/**
 * `EditDocumentContentComponent` — smart component responsible for displaying,
 * editing, and recalculating the line items of a single document.
 *
 * ### Responsibilities
 * - Loads document items via {@link DocumentApiService.getDocItemsByDoc} and enriches them
 *   through a single batch call ({@link DocumentApiService.getDocItemsEnriched}).
 * - Calculates per-row prices, profit, and status flags synchronously where possible,
 *   and asynchronously (HTTP) only when a quantity change requires fresh income-price data.
 * - Maintains an editable copy of rows ({@link docItemsCopy}) to support cancel/undo.
 * - Aggregates document-level totals via {@link calculateDocumentSummaryObservable}.
 * - Exposes `@Output()` events so the parent can track editing state and document health.
 *
 * ### Observable pipeline (load)
 * ```
 * reloadDocContent()
 *   → docItemList$.next()
 *   → getDocItemsByDoc()
 *   → calculateDocItemsFieldsObservable()
 *     → getDocItemsEnriched()  (single batch HTTP)
 *     → applyEnrichedData()
 *     → calculateDiRowProfitSync()  per row
 *     → calculateDiRowStatusObservable()  per row
 *   → calculateDocumentSummaryObservable()
 * ```
 *
 * ### Observable pipeline (single-row fast refresh)
 * ```
 * fastReloadDocContent(di)
 *   → pullDiRowPriceAndStockObservable(di)
 *   → calculateDiRowProfitObservable(di)
 *     → pullDiRowStockIncomePriceObservable(di)
 *     → calculateDiRowProfitSync(di)
 *   → calculateDocumentSummaryObservable()
 * ```
 *
 * @selector `app-edit-document-content`
 */
@Component({
  selector: 'app-edit-document-content',
  templateUrl: './edit-document-content.component.html',
  styleUrls: ['./edit-document-content.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class EditDocumentContentComponent implements OnInit, OnDestroy, OnChanges {

  // Add error tracking
  private errorCount: number = 0;
  private readonly MAX_ERRORS = 5;

  private globalSettinsData: GlobalSettingsData = null;

  private docItemQuery: Subscription = null;
  private docItemList$ = new Subject<number>();

  private diSumQuery: Subscription = null;
  private diSaveQuery: Subscription = null;
  private diGroupQuery: Subscription = null;

  private _prodShippedQuery: Subscription = null;

  //private calcDocSum$ = new Subject<Document>();
  //private calcDocSumSubs: Subscription = null;

  //private calcDocProfit$ = new Subject<DocItems[]>();
  //private calcDocProfitSubs: Subscription = null;

  //private calcDocItemProfit$ = new Subject<DocItems>();
  //private calcDocItemProfitSubs: Subscription = null;

  private activeSubscriptions: Subscription[] = [];
  private recalcSubscriptions = new Map<number, Subscription>();
  private destroy$ = new Subject<void>();
  private qtyChange$ = new Subject<DocItems>();

  private ms: number = 0;
  private is: number = 0;
  private as: number = 0;

  /** Manufacturing/assembly site (`globalSettinsData.manDepartmentId`) — informational only in
   *  {@link openIncomePriceBreakdown}'s depot list. `calculateDiRowProfitSync`'s blend never
   *  includes it (only Main/Import/Aux), so it must never feed `coveredQty`/`excessQty`/
   *  `stockPrice`/`fallbackPrice`; see that method's comment on why not. */
  private mfg: number = 0;

  /** When `false`, the component's own header bar is hidden. */
  @Input() showHeader: boolean = true;
  /** When `false`, the editing toolbar is disabled. */
  @Input() allowEditControls: boolean = true;
  /** When `false`, the payment-sum footer is hidden. */
  @Input() showPaysum: boolean = true;

  /** When `true`, zero-qty rows are included in totals and printing forms. */
  @Input() countEmpty: boolean = false;

  /** Two-way binding partner for `countEmpty`. */
  @Output() countEmptyChange: EventEmitter<boolean> = new EventEmitter();

  loading: boolean = false;
  contentIsLoading: boolean = false;

  typeDeleteConfirmation: boolean = false;
  confirmText: string = 'DELETE 12';
  deleteAllRows: boolean = false;

  selectedDocType: number = 0;

  sDI: DocItems = null;

  allDocItemsChanged: boolean = false;

  docItems: DocItems[] = [];

  docItemsCopy: DocItems[] = null;
  docItemsClone: DocItems[] = [];

  u4etkaMultiplier: number = 1;

  yesno = [
    { label: 'Да', value: 'Да' },
    { label: 'Нет', value: 'Нет' },
    { label: '', value: '' }
  ];

  @ViewChild('docItemTable') docItemTable: Table;
  @ViewChild('priceBreakdownDialog') priceBreakdownDialog: IncomePriceBreakdownDialogComponent;


  // https://github.com/primefaces/primeng/issues/9893
  //
  // possible solution:
  // https://github.com/orgs/primefaces/discussions/438
  //

  //@HostListener('keydown.space', ['$event'])
  //onSpaceKeyDown(event: KeyboardEvent) {
  //  event.stopPropagation();
  //  event.preventDefault();
  //  if (event.target) {
  //    event.target['value'] = event.target['value'] + ' ';
  //    (event.target as HTMLElement).focus();
  //  }
  //}

  editMode: boolean = false;
  confirmDeleteOn: boolean = false;

  /** The document whose line items are displayed and edited. Changing this triggers a full reload. */
  @Input() inputDocument: Document = null;

  /** Reserved trigger input — currently unused. */
  @Input() knockKnock: boolean = false;

  /**
   * `inputDocument.DOCCLIENTSID`'s customer-level discount tier (2026-09-21), refetched
   * whenever `inputDocument` changes (see `ngOnChanges`). Read synchronously in
   * `onProductSelection()` rather than fetched per row -- every row in one document shares
   * the same customer, and `DocItemPricingService.priceRow()` (the *other* place a row gets
   * priced, in the `new-document-dlg-doc-items` "create new document" flow) already
   * established the one-lookup-per-document pattern this mirrors. `null` for an untiered
   * customer, a lookup failure, or before the first fetch resolves -- all three price at
   * catalogue retail, same fail-toward-no-discount fallback `getTierDiscountPercent()` uses.
   */
  private customerTier: AgentCustomerTier | null = null;
  private customerTierQuery: Subscription = null;

  private storeNameShowRowOptions: string = 'edit-document-table-columns-1';

  showDocRowsOptions = [
    { name: 'Учетка', value: 1 },
    { name: 'Без НДС', value: 2 },
    { name: 'УКТЗЕД', value: 4 },
    { name: 'Резерв', value: 8 },
    { name: 'Примечание', value: 16 },
    { name: 'Доходность', value: 32 },
  ];

  showFilterOptions = [
    { name: 'Инвойс', icon: 'pi pi-filter', value: 1, disabled: false },
  ];

  showFilterValue: number[] = [1];

  public get showFilter(): boolean { return this.showFilterValue.includes(1); }

  sU4etka: boolean = false;

  allowOld: boolean = false;

  profitMode: boolean = false;

  /** Bitmask controlling which optional columns are visible; overrides the localStorage value when non-empty. */
  @Input() setShowRowOptions: number[] = [];

  showRowOptions: number[] = [];

  public get noItems(): boolean { return ((this.docItems || []).length < 1); }

  items: MenuItem[] = [
    {
      label: 'Минимум', icon: 'pi pi-star-o', disabled: false, command: () => {
        this.doSetDiscount('min');
      }
    },
    {
      label: 'Средняя', icon: 'pi pi-star', disabled: false, command: () => {
        this.doSetDiscount('mid');
      }
    },
    {
      label: 'Максимум', icon: 'pi pi-sun', disabled: false, command: () => {
        this.doSetDiscount('max');
      }
    },
    {
      label: 'Убрать', icon: 'pi pi-trash', disabled: false, command: () => {
        this.doSetDiscount('non');
      }
    },
  ];

  excelExportItems: MenuItem[] = [
    {
      label: 'С доходом', icon: 'pi pi-star-o', disabled: false, command: () => {
        this.onExportDataWithProfit();
      }
    },
    {
      label: 'С логисткой', icon: 'pi pi-star-o', disabled: false, command: () => {
        this.onExportDataWithLogistic();
      }
    }
  ];

  public get canSeeFinanceResult(): boolean { return (this.auth.isUserInRole('portal-admin') || this.auth.isUserInRole('finance-manager') || this.auth.isUserInRole('allow-see-profit')); }

  /*
   *  TODO: load access right to check is U4ETKA is visible to
   */
  public get canSeeU4etka(): boolean { return this.canSeeFinanceResult && ([1].includes(this.inputDocument.DOCTYPE) && this.showRowOptions.includes(1)); }

  public get seeWoVAT(): boolean { return [1, 12].includes(this.inputDocument.DOCTYPE) && this.showRowOptions.includes(2); }

  public get seeTariffCode(): boolean { return [1, 12].includes(this.inputDocument.DOCTYPE) && this.showRowOptions.includes(4); }

  public get seeReservation(): boolean { return [1, 12].includes(this.inputDocument.DOCTYPE) && this.showRowOptions.includes(8); }

  public get seeProfit(): boolean { return this.canSeeFinanceResult && [1, 12].includes(this.inputDocument.DOCTYPE) && this.showRowOptions.includes(32); }

  public get seeDiNote(): boolean { return ([1, 12].includes(this.inputDocument.DOCTYPE) && this.showRowOptions.includes(16)); }

  /**
   * True only when a row is selected AND still on screen. A selected row that the
   * "empty rows" switch has since hidden must not stay targetable by delete / add-row,
   * or the user would act on a row they cannot see. (`sDI` is kept, so the row is
   * selected again if it becomes visible.)
   */
  public get selection(): boolean { return !!this.sDI && this.isDocItemRowVisible(this.sDI, false); }
  public get changes(): boolean { return this.editMode || this.allDocItemsChanged; }

  public get showShipped(): boolean { return !this.editMode && [1].includes(this.inputDocument.DOCTYPE); }

  public get allowTopButtons(): boolean { return this.docItems && (this.docItems.length > 10); }

  public get exportDisabled(): boolean { return this.noItems || this.editMode || this.contentIsLoading; }

  public get noAddAbove(): boolean { return this.editMode || this.contentIsLoading || !this.selection; }
  public get noAddBelow(): boolean { return this.editMode || this.contentIsLoading || !this.selection; }

  public allowSave(di: DocItems): boolean { return di && (di.PRODID ? true : false); }

  cf3: any = {};

  /** Emits `true` while any row is in edit mode or bulk changes are pending. */
  @Output() editing: EventEmitter<boolean> = new EventEmitter();
  /** Mirrors the internal `contentIsLoading` flag for parent spinners. */
  @Output() contentLoading: EventEmitter<boolean> = new EventEmitter();

  documentItemsStatus: number = 101;

  /** Emits `0` = clean document, `1` = at least one highlighted row. */
  @Output() getDocumentStatus: EventEmitter<number> = new EventEmitter();

  // TODO: Add checking for complex products
  // Disable by deafult the Edit and Remove buttons for such products
  // Special case RemocveAll button should be enabled

  constructor(
    private prodApi: ProductApiService,
    private auth: KeycloakService,
    private messageService: MessageService,
    private docApi: DocumentApiService,
    private cliApi: ClientApiService,
    private globalsettingsservice: GetglobalsettingsService,
    private filterService: FilterService,
  ) {

    this.docItemQuery =
      this.docItemList$.pipe(

        takeUntil(this.destroy$),
        debounceTime(300),

        tap(docId => {
          this.contentIsLoading = true;
          this.contentLoading.emit(this.contentIsLoading);
          console.log('*** EditDocumentContentComponent docItemList$ subscribing....: ' + docId)
        }),

        switchMap(docId => this.docApi.getDocItemsByDoc(docId).pipe(
          catchError(this.handleError('loadDocItems', [])),

          tap(data => environment.production || console.table(data)),
          tap(data => this.docItems = data),
          filter(data => data?.length > 0),
          filter(_ => this.canProceed()),

          tap(data => {
            data.forEach(di => {
              di.cloneIndex = 0; // Reset clone index on reload - moved here from suspicious place
            });
          }),

          concatMap(data => this.calculateDocItemsFieldsObservable(data)),

          //map(data => {
          //  console.log('*** EditDocumentContentComponent docItemList$ subscription: ')

          //  this.calculateDocItemsFields(data);     // <= Raw docItems[] from database
          //  return data;
          //}),

          toArray(),
          concatMap(() => this.calculateDocumentSummaryObservable()),

          finalize(() => {
            //  this.calculateDocumentSummary();        // <= Processed docItems[] with all calculated fields
            //  // <= Here we have also document summary calculated
            this.contentIsLoading = false;
            this.contentLoading.emit(this.contentIsLoading);

            this.allDocItemsChanged = false;
            this.editing.emit(this.allDocItemsChanged);
          })
        ))
      )
        .subscribe({
          next: () => {
            console.log('*** EditDocumentContentComponent docItemList$ subscription done');
          },
          error: (error) => {
            console.error('*** EditDocumentContentComponent docItemList$ subscription error:', error);
          }
        });
  }

  /**
   * Centralized RxJS error handler factory.
   *
   * Increments {@link errorCount}, shows a PrimeNG toast with a user-friendly message,
   * clears the loading spinner, and returns a safe fallback observable so the stream
   * can continue instead of terminating.
   *
   * @param operation - A key used to look up the friendly message in {@link getUserFriendlyError}
   *   and to label the `console.error` output.
   * @param result - Optional fallback value emitted by the returned observable.
   *   When omitted the returned observable completes immediately ({@link EMPTY}).
   * @returns A function `(error) => Observable<T>` suitable for `catchError()`.
   */
  private handleError<T>(operation: string, result?: T) {
    return (error: any): Observable<T> => {
      this.errorCount++;

      console.error(`${operation} failed:`, error);

      // Log to monitoring service (if available)
      // this.errorLoggingService.logError(error);

      // Show user-friendly message
      this.messageService.add({
        severity: 'error',
        summary: 'Помилка операції',
        detail: this.getUserFriendlyError(operation, error),
        life: 5000
      });

      // Stop loading indicators
      this.contentIsLoading = false;
      this.contentLoading.emit(false);

      // Return safe fallback
      return result ? of(result) : EMPTY;
    };
  }

  private getUserFriendlyError(operation: string, error: any): string {
    const errorMap: { [key: string]: string } = {
      'calculateDocItemsFields': 'Не вдалося завантажити дані документа',
      'saveDocItem': 'Не вдалося зберегти позицію',
      'deleteDocItem': 'Не вдалося видалити позицію',
      'loadProduct': 'Не вдалося завантажити дані товару',
      'calculateProfit': 'Помилка розрахунку прибутку',
      'exportData': 'Помилка експорту даних'
    };

    const message = errorMap[operation] || 'Невідома помилка';

    if (error.status === 0) {
      return `${message}: Немає з'єднання з сервером`;
    }
    if (error.status === 401) {
      return `${message}: Немає авторизації`;
    }
    if (error.status === 403) {
      return `${message}: Немає прав доступу`;
    }
    if (error.status === 404) {
      return `${message}: Дані не знайдено`;
    }
    if (error.status >= 500) {
      return `${message}: Помилка сервера`;
    }

    return message;
  }

  /**
   * Guards the observable pipeline against runaway error loops.
   *
   * Returns `false` and shows a sticky error toast once {@link errorCount} reaches
   * {@link MAX_ERRORS}, preventing further HTTP calls and calculations.
   *
   * @returns `true` if processing should continue, `false` otherwise.
   */
  private canProceed(): boolean {
    if (this.errorCount >= this.MAX_ERRORS) {
      this.messageService.add({
        severity: 'error',
        summary: 'Критична помилка',
        detail: 'Занадто багато помилок. Перезавантажте сторінку.',
        sticky: true
      });
      return false;
    }
    return true;
  }

  // procedure to reload document content only from change of single Di row
  // without full reload of document items
  // Would allow faster editing of document items and better user experience (response time)

  // Updated fastReloadDocContent
  /**
   * Refreshes a **single** document-item row without reloading the entire list.
   *
   * Useful after saving a row edit ({@link onDiRowEditSave}) or cancelling an edit
   * ({@link onDiRowEditCancel}).  The sequence is:
   * 1. {@link pullDiRowPriceAndStockObservable} — re-fetches price / shipped data.
   * 2. {@link calculateDiRowProfitObservable} — recalculates profit with fresh stock prices.
   * 3. {@link calculateDocumentSummaryObservable} — updates document-level totals.
   *
   * Sets `contentIsLoading` for the duration and saves a fresh {@link docItemsCopy}.
   *
   * @param di - The document item row to refresh.
   */
  fastReloadDocContent(di: DocItems) {
    console.log('*** EditDocumentContentComponent fastReloadDocContent: ' + JSON.stringify(di))

    if (this.inputDocument) {
      this.contentIsLoading = true;
      this.contentLoading.emit(this.contentIsLoading);

      const sub = this.pullDiRowPriceAndStockObservable(di).pipe(
        map(di => {
          environment.production || console.log('*** EditDocumentContentComponent pullDiRowPriceAndStockObservable.then: ' + JSON.stringify(di));
          let idx = this.docItems.findIndex(d => d.DOCITEMID == di.DOCITEMID);
          if (idx >= 0) {
            this.docItems[idx] = di;
          }
          return di;
        }),

        concatMap(di => this.calculateDiRowProfitObservable(di)),
        concatMap(() => this.calculateDocumentSummaryObservable()),

        catchError(error => {
          console.error('fastReloadDocContent error:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Помилка оновлення',
            detail: 'Не вдалося оновити рядок документа'
          });
          return EMPTY;
        }),

        finalize(() => {
          this.inputDocument.items = this.docItems;
          this.saveDiCopy();
          this.contentIsLoading = false;
          this.contentLoading.emit(this.contentIsLoading);
        })
      ).subscribe({
        error: (error) => {
          console.error('fastReloadDocContent subscription error:', error);
        }
      });

      this.addSubscription(sub);
    }
  }

  /**
   * Registers a subscription in {@link activeSubscriptions} so it is automatically
   * unsubscribed in {@link ngOnDestroy}.
   *
   * @param sub - The subscription to track.
   */
  private addSubscription(sub: Subscription): void {
    this.activeSubscriptions.push(sub);
  }

  /**
   * Safely replaces a long-lived subscription reference.
   *
   * Unsubscribes from `existingRef` (if any), registers `newSub` in
   * {@link activeSubscriptions}, and returns `newSub` so callers can store it.
   *
   * @param existingRef - The subscription to cancel (may be `null`).
   * @param newSub      - The new subscription to track.
   * @returns `newSub`.
   */
  private reassignSubscription(
    existingRef: Subscription | null,
    newSub: Subscription
  ): Subscription {
    existingRef?.unsubscribe();
    this.activeSubscriptions.push(newSub);
    return newSub;
  }

  /**
   * Refetches {@link customerTier} for `doc.DOCCLIENTSID` (2026-09-21). Fetched once per
   * document/customer, not per row -- `onProductSelection()` reads the cached value
   * synchronously. A missing `DOCCLIENTSID` (no buyer picked yet) or a failed lookup both
   * reset `customerTier` to `null`, pricing every row at catalogue retail until a real
   * tier is known -- the same fail-toward-no-discount direction `getTierDiscountPercent()`
   * and `DocItemPricingService.priceRow()` both already take.
   */
  private refreshCustomerTier(doc: Document | null): void {
    if (!doc?.DOCCLIENTSID) {
      this.customerTier = null;
      return;
    }

    this.customerTierQuery = this.reassignSubscription(
      this.customerTierQuery,
      this.cliApi.getDiscountTier(doc.DOCCLIENTSID).pipe(
        catchError(() => of({ tier: null as AgentCustomerTier | null })),
      ).subscribe(result => { this.customerTier = result?.tier ?? null; })
    );
  }

  /**
   * Triggers a full reload of the document-item list by pushing the current
   * document ID into the {@link docItemList$} subject.
   *
   * The subject is debounced (300 ms) so rapid successive calls are coalesced.
   *
   * @param event - DOM or PrimeNG event (not used directly; may be `null`).
   */
  reloadDocContent(event) {
    console.log('*** EditDocumentContentComponent reloadDocContent: ' + JSON.stringify(event))

    if (this.inputDocument) {
      this.docItemList$.next(this.inputDocument.DOCID);
    }
  }

  checkCommand(event) {
    this.items.forEach(e => e.disabled = this.noItems);
  }

  buildDiscountOptionsMenu(di: DocItems) {

    di.doptions = [
      { label: formatNumber(0, 'uk', '1.0-0') + '%', value: 0 },
    ];
    if (di.prodInfo) {
      di.doptions = [
        ...di.doptions,
        { label: formatNumber(di.prodInfo.PRODBASEDISC, 'uk', '1.0-0') + '%', value: di.prodInfo.PRODBASEDISC },
        { label: formatNumber(di.prodInfo.PRODQTYDISC, 'uk', '1.0-0') + '%', value: di.prodInfo.PRODQTYDISC },
        { label: formatNumber(di.prodInfo.PRODDIALDISC, 'uk', '1.0-0') + '%', value: di.prodInfo.PRODDIALDISC },
      ];
    }
  }

  // Save current docItems to docItemsCopy
  // Simple deep copy to allow restoration later

  /**
   * Takes a shallow snapshot of {@link docItems} into {@link docItemsCopy}.
   * Called after every successful load or fast-refresh so that {@link restoreDiCopy}
   * and edit-cancel operations have a known-good baseline to roll back to.
   */
  saveDiCopy() {
    this.docItemsCopy = [];
    this.docItems.forEach(e => this.docItemsCopy.push({ ...e }));
  }

  // Restore docItems from docItemsCopy
  // Using RxJS to handle the restoration process
  // This approach ensures that the restoration is done asynchronously

  /**
   * Restores {@link docItems} from the snapshot saved by the last {@link saveDiCopy} call.
   *
   * Rebuilds the array asynchronously via `from()` + `toArray()` to keep the UI
   * responsive on large documents, then recalculates document-level summaries.
   * Clears `allDocItemsChanged` and emits `false` on {@link editing} when done.
   */
  restoreDiCopy() {
    this.docItems = [];

    this.contentIsLoading = true;
    this.contentLoading.emit(this.contentIsLoading);

    this.addSubscription(
      from(this.docItemsCopy).pipe(
        map(e => (this.docItems.push({ ...e }))),
        toArray(),

        tap(s => environment.production || console.log('*** EditDocumentContentComponent restoreDiCopy: ' + JSON.stringify(s))),

        concatMap(() => this.calculateDocumentSummaryObservable()),

        catchError(error => {
          console.error('restoreDiCopy error:', error);
          return EMPTY;
        }),

        finalize(() => {
          this.allDocItemsChanged = false;
          this.editing.emit(this.allDocItemsChanged);

          this.contentIsLoading = false;
          this.contentLoading.emit(this.contentIsLoading);
        })

      ).subscribe({
        error: (error) => {
          console.error('restoreDiCopy subscription error:', error);
        }
      })
    );

    //this.docItemsCopy.forEach(e => this.docItems.push({ ...e }));
  }

  // Updated calculateDocItemsFieldsObservable
  // Refactored to use RxJS operators for better readability and maintainability
  // Here we process each DocItems asynchronously and update the document items accordingly
  // This procedure pulls price and stock info for each DocItems row
  // and calculates all necessary fields
  // Finally, it updates the inputDocument items and triggers profit calculation

  // Observable chain sequence:
  // reloadDocContent() → docItemList$.next() →
  //   getDocItemsByDoc() → calculateDocItemsFieldsObservable() →
  //     pullDiRowPriceAndStockObservable() →
  //       calculateDiRowProfitObservable() →
  //         pullDiRowStockIncomePriceObservable() →
  //           calculateDiRowStatusObservable() →
  //             calculateDocumentSummaryObservable() →
  //               (emits document status)

  // Procedure calculateDocItemsFieldsObservable calls the following methods in sequence for each DocItems row:
  // 1. pullDiRowPriceAndStockObservable - pulls price and stock info for the row
  // 2. calculateDiRowProfitObservable - calculates profit related fields for the row
  // 3. calculateDiRowStatusObservable - calculates status flags for the row
  // After processing all rows, it updates the inputDocument items
  // and triggers document summary calculation

  // We have also pullDiRowStockIncomePriceObservable and pullDiRowPriceAndStockObservable methods
  // Looks like both do similar things but pullDiRowPriceAndStockObservable is more complete
  // and used in more places
  // pullDiRowPriceAndStockObservable → calculateDiRowProfitObservable → calculateDiRowStatusObservable

  // Each step is handled asynchronously with proper error handling
  // and loading state management
  // This approach ensures that the UI remains responsive
  // and that all data is processed correctly before updating the document
  // and triggering summary calculations
  // This method returns an Observable that emits the fully processed DocItems array
  // once all rows have been processed
  // This allows for better integration with other RxJS-based workflows in the application
  // and ensures that all asynchronous operations are properly chained and managed
  // This refactoring improves code clarity and maintainability
  // while preserving the original functionality and logic of the document item processing
  // This method is a key part of the document editing workflow
  // and ensures that all document items are accurately calculated and updated
  // based on the latest data from the backend services
  // This method is called whenever the document items need to be recalculated
  // such as after loading the document or after editing individual items
  // This method is essential for maintaining data integrity
  // and ensuring that all calculations are up-to-date
  // and reflects the current state of the document and its items
  // This method is designed to be efficient and scalable
  // allowing for smooth handling of documents with large numbers of items
  // while ensuring accurate calculations and updates
  // This method is a critical component of the document management system
  // and plays a vital role in ensuring the accuracy and reliability of document data
  // throughout the application

  // Here we come with DocItems raw data from database pulled by getDocItemsByDoc API call
  // Each DocItems row contains raw fields as below:
  //  DocItems[] {
  //  DOCITEMID: number,
  //  DOCID: number,
  //  DOCTYPE: number,
  //  PRODID: number,
  //  DOCITEMPRODNAME: string,
  //  DOCITEMPRICE: number,
  //  DOCITEMQTY: number,
  //  DOCITEMVPRICE: number,
  //  DOCITEMCURRID: number,
  //  DOCITEMCRATE: number,
  //  PRODVAT: number,
  //  PRODCODE: string,
  //  PRODCUSTCODE: string,
  //  PAYITEMCLIID: number,
  //  U4ETKA: number,
  //  DOCINCOMEPRICE: number,
  //  // ... other raw fields
  //}

  // We need to process each row to pull additional data:
  // - Price info
  // - Stock info
  // And calculate necessary fields:
  // - DOCITEMSUMWOVAT
  // - DOCITEMSUM
  // - DOCITEMVSUM
  // - DOCITEMPROFIT
  // - DOCITEMPROFITSUM
  // - Status flags (bip, mst, cpn, lpf, etc.)
  // Finally, we need to update the inputDocument items
  // and trigger document summary calculation
  // This method returns an Observable that emits the fully processed DocItems array

  /**
   * Enriches a raw `DocItems[]` array returned by the API with all calculated fields
   * needed for display and editing.
   *
   * **Step 1 — batch HTTP** — calls {@link DocumentApiService.getDocItemsEnriched} once
   * for the whole document (replaces N × 8 individual per-row requests) and merges the
   * result with raw rows via {@link applyEnrichedData}.
   *
   * **Step 2 — synchronous math** — runs {@link calculateDiRowProfitSync} and
   * {@link calculateDiRowSum} for every row without any further HTTP calls.
   *
   * **Step 3 — status flags** — pipes each row through
   * {@link calculateDiRowStatusObservable} (synchronous mapping, wrapped in `of()`).
   *
   * On completion updates {@link inputDocument}.items, saves {@link docItemsCopy}, and
   * clears the loading spinner.
   *
   * @param data - Raw `DocItems[]` freshly loaded from the database.
   * @returns An observable that emits the fully processed `DocItems[]` once.
   */
  calculateDocItemsFieldsObservable(data: DocItems[]): Observable<DocItems[]> {

    return of(data).pipe(

      tap(() => {
        this.contentIsLoading = true;
        this.contentLoading.emit(this.contentIsLoading);
      }),

      // Single batch call — replaces N x 8 per-row HTTP requests
      switchMap(rawItems => this.docApi.getDocItemsEnriched(
        this.inputDocument.DOCID,
        this.inputDocument.DOCMANCLIID,
        this.inputDocument.DOCCURRID,
        this.ms,
        this.is,
        this.as,
        new Date()
      ).pipe(
        catchError(this.handleError('getDocItemsEnriched', [])),
        map(enriched => {
          const items = applyEnrichedData(rawItems, enriched, this.globalSettinsData.vatValue, this.inputDocument.DOCRATEVAL);
          items.forEach(di => this.buildDiscountOptionsMenu(di));
          return items;
        })
      )),

      // Calculate profit (synchronous math using enriched income prices)
      switchMap(items =>
        from(items).pipe(
          map(di => this.calculateDiRowProfitSync(di)),
          map(di => this.calculateDiRowSum(di)),
          toArray()
        )
      ),

      // Calculate status flags (synchronous, no HTTP)
      switchMap(items =>
        from(items).pipe(
          concatMap(di => this.calculateDiRowStatusObservable(di)),
          toArray()
        )
      ),

      tap(calculatedItems => { this.docItems = [...calculatedItems]; }),
      filter(() => this.canProceed()),

      catchError(error => {
        console.error('calculateDocItemsFieldsObservable error:', error);
        return EMPTY;
      }),

      finalize(() => {
        environment.production || console.log('*** EditDocumentContentComponent calculateDocItemsFieldsObservable => finalize(docItems)');
        this.inputDocument.items = this.docItems;
        this.saveDiCopy();
        this.contentIsLoading = false;
        this.contentLoading.emit(this.contentIsLoading);
      })
    );
  }

  // Updated pullDiRowPriceAndStockObservable
  // Refactored to use RxJS operators for better readability and maintainability
  // This method pulls price and stock info for a single DocItems row
  // and calculates necessary fields

  // Data Dependencies Chain:

  //  pullDiRowPriceAndStockObservable(di)
  //  ↓ Sets: di.prodInfo, di.DOCITEMSHIPPED, di.PRODOEMNAME, etc.
  //  ↓ Sets: di.DOCINCOMEPRICE (initial/simple value)

  //calculateDiRowProfitObservable(di)
  //  ↓ Calls: pullDiRowStockIncomePriceObservable(di)
  //    ↓ Requires: di.prodInfo(from previous step)
  //    ↓ Sets: di.prodInfo.PRODSTOCK, PRODSTOCKI, PRODSTOCKA, PRODSTOCKM
  //    ↓ Sets: di.prodInfo.PRODINPRICEA, PRODINPRICEI, PRODINPRICEM
  //    ↓ Overwrites: di.DOCINCOMEPRICE(with weighted calculation)
  //  ↓ Sets: di.DOCITEMPROFIT, di.DOCITEMPROFITSUM

  //calculateDiRowStatusObservable(di)
  //  ↓ Requires: di.DOCINCOMEPRICE, di.DOCITEMPROFIT(from previous steps)
  //  ↓ Sets: di.bip, di.lpf, di.hlt, etc. (status flags)

  /**
   * Fetches all external data needed for a single document-item row in **parallel**
   * using `forkJoin`, then merges the results back onto `di`.
   *
   * Parallel calls:
   * - `getDocItemsShippedByDoc` → `di.DOCITEMSHIPPED`
   * - `getProdPriceInfoById`    → `di.prodInfo`, retail/discount fields
   * - `getProductById`          → `di.PRODOEMNAME`, `di.PRODWWWPAGE`, `di.npc`
   * - `getActcListById`         → `di.nta` (tariff-code confirmation flag)
   *
   * Each inner call has a 10 s timeout with one automatic retry and falls back via
   * {@link handleError}.
   *
   * @param di - The document item row to enrich (mutated in place and returned).
   * @returns An observable that emits the enriched `di`.
   */
  pullDiRowPriceAndStockObservable(di: DocItems): Observable<DocItems> {
    // di.cloneIndex = 0;         // Reset clone index on reload - very suspicious place to do that
    // better place to move this line just after getting docItems from database

    di.PRODVAT = (di.PRODVAT ?? this.globalSettinsData.vatValue);
    di.DOCITEMPRICEWOVAT = round1(di.DOCITEMPRICE / ((100.0 + di.PRODVAT) / 100.0));
    di.DOCITEMPRICE = round2(di.DOCITEMPRICEWOVAT * ((100.0 + di.PRODVAT) / 100.0));

    return forkJoin({
      shipped: this.docApi.getDocItemsShippedByDoc(di.DOCITEMID, new Date()).pipe(
        timeout(10000),
        retry(1),
        catchError(this.handleError('loadShippedInfo', [{ DOCITEMSHIPPED: 0 }]))
      ),
      priceInfo: this.prodApi.getProdPriceInfoById(
        di.PRODID,
        this.inputDocument.DOCMANCLIID,
        this.inputDocument.DOCCURRID,
        this.ms,
        this.is,
        this.as,
        new Date()
      ).pipe(
        timeout(10000),
        retry(1),
        catchError(this.handleError('loadPriceInfo', []))
      ),
      product: this.prodApi.getProductById(di.PRODID).pipe(
        timeout(10000),
        retry(1),
        catchError(this.handleError('loadProduct', []))
      ),
      actc: this.prodApi.getActcListById(di.PRODID).pipe(
        timeout(10000),
        retry(1),
        catchError(this.handleError('loadActc', []))
      )
    }).pipe(
      filter(_ => this.canProceed()),

      map(results => {

        if (results) {
          // Process shipped data
          if (results.shipped?.length > 0) {
            di.DOCITEMSHIPPED = results.shipped[0].DOCITEMSHIPPED;
          }

          // Process price info
          if (results.priceInfo?.length > 0) {
            // Initialize with defaults
            di.prodInfo = results.priceInfo[0] || {
              PRODPRICE: 0,
              PRODINPRICE: 0,
              PRODSTOCK: 0,
              PRODBASEDISC: 0,
              PRODQTYDISC: 0,
              PRODDIALDISC: 0
            };
            //            di.DOCINCOMEPRICE = di.prodInfo.PRODINPRICE * this.inputDocument.DOCRATEVAL;
            di.DOCITEMRETVPRICE = di.prodInfo.PRODPRICE;
            di.DOCITEMRETPRICE = di.DOCITEMRETVPRICE * this.inputDocument.DOCRATEVAL;
            di.DISCOUNT = round2((1 - (di.DOCITEMVPRICE / (di.DOCITEMRETVPRICE))) * 100);

            this.buildDiscountOptionsMenu(di);
          }

          // Process product data
          if (results.product?.length > 0) {
            let ppi = results.product[0];
            di.PRODOEMNAME = ppi.PRODVENDORNAME;
            di.PRODWWWPAGE = ppi.PRODWWWPAGE;
            di.PRODBARCODEMASK = ppi.PRODBARCODEMASK;
            di.npc = (ppi.PRODCATEGORY || '') == '';
          }

          // Process ACTC data
          di.nta = true;
          if (results.actc?.length > 0) {
            di.nta = !(di.PRODCUSTCODE == results.actc[0].ACTC_STRING);
          }

          // Finally, calculate row status
          // Here we have all data loaded - shipped, price info, product, actc
        }
        return di;
      })
    );
  }

  // Updated pullDiRowStockIncomePriceObservable
  // Refactored to use RxJS operators for better readability and maintainability
  // This method pulls stock and income price info for a single DocItems row
  // and calculates necessary fields
  // Used in calculateDiRowProfitObservable to separate concerns and improve modularity
  // *** Calculate pure income price *** based on stock levels and recent prices for particular quantity

  /**
   * Resolves the "as of" date for this row's income-price FIFO lookup — used by both
   * {@link pullDiRowStockIncomePriceObservable} (the live `PRODINPRICEM/I/A` fetch behind
   * `DOCINCOMEPRICE`) and {@link openIncomePriceBreakdown} (the read-only popup), so the two
   * can never legitimately disagree over which date they priced as of (only PR #185's
   * MISMATCH_TOLERANCE absorbs rounding noise, not a genuine different-date discrepancy).
   *
   * Owner's rule (2026-09-03): `edit-document-content` only ever handles DOCTYPE 1/12
   * (invoice/proforma) — `edit-document-content-receipt`/`-inventory` already use the
   * document's own `DOCDATE` for DOCTYPE 4/5, which is correct as-is and out of scope here.
   * For an invoice/proforma line: default to **today** UNLESS this product has already been
   * shipped out under this same document, in which case use the date of the **most recent**
   * such shipment — the item's cost basis was fixed the moment it actually left the
   * warehouse, not whenever someone happens to reopen the document afterward.
   *
   * "Already shipped under this document" is resolved via `PRIMDOCID` — the same parent/child
   * document link `edit-document-content-receipt.calculateRowSummary` already relies on for
   * its own `pdp` (primary-doc-price-parity) check — not by any per-item shipped-quantity
   * field, since none is populated on this component's own `DocItems` today. `getChildDocList`
   * returns this invoice's direct children; each DOCTYPE=4 (shipment) child is checked via
   * `getDocItemsByDoc` for a row with this `PRODID`. DOCTYPE=5 (transfer) is deliberately
   * excluded — that's a stock movement between depots, not a shipment to a customer. No
   * matching shipment (none yet, or none contain this product) falls back to `new Date()`.
   */
  private resolveIncomePriceAsOfDate$(prodId: number): Observable<Date> {
    const invoiceId = this.inputDocument?.DOCID;
    if (!invoiceId) {
      return of(new Date());
    }

    return this.docApi.getChildDocList(invoiceId).pipe(
      switchMap(children => {
        const shipments = (children || []).filter(c => c.DOCTYPEID === 4 && c.DOCID);
        if (shipments.length === 0) {
          return of(new Date());
        }

        return forkJoin(
          shipments.map(ship => this.docApi.getDocItemsByDoc(ship.DOCID).pipe(
            map(items => ((items || []).some(it => it.PRODID === prodId) ? ship.DOCDATE : null)),
            catchError(() => of(null as Date | null)),
          ))
        ).pipe(
          map(dates => {
            const matched = dates.filter((d): d is Date => d !== null && d !== undefined);
            return matched.length > 0
              ? matched.reduce((latest, d) => (new Date(d) > new Date(latest) ? d : latest))
              : new Date();
          })
        );
      }),
      catchError(() => of(new Date())),
    );
  }

  /**
   * Fetches per-depot stock levels and per-qty income prices for a single row.
   *
   * Populates `di.prodInfo` with:
   * - `PRODSTOCK`, `PRODSTOCKM`, `PRODSTOCKI`, `PRODSTOCKA` — depot stock quantities.
   * - `PRODINPRICEM`, `PRODINPRICEI`, `PRODINPRICEA` — income prices per depot,
   *   converted to main currency via `DOCRATEVAL`.
   *
   * **Important:** this method does **not** set `di.DOCINCOMEPRICE`.  The final
   * effective per-unit income price (FIFO weighted average with excess-qty fallback)
   * is computed by the immediately following {@link calculateDiRowProfitSync} call.
   *
   * Called by {@link calculateDiRowProfitObservable} (single-row async path).
   *
   * Stock levels (`PRODSTOCKM/I/A`, fetched below) stay as-of **now** — "how much is
   * physically on hand" is inherently a live quantity, distinct from the "as of" date
   * question {@link resolveIncomePriceAsOfDate$} answers for the price lookups themselves.
   *
   * @param di - The document item row to enrich (mutated in place and returned).
   * @returns An observable that emits the enriched `di`.
   */
  pullDiRowStockIncomePriceObservable(di: DocItems): Observable<DocItems> {
    //environment.production || console.log('*** EditDocumentContentComponent pullDiRowStockIncomePriceObservable: ' + JSON.stringify(di))
    return of(di).pipe(

      concatMap(di => this.prodApi.getProdStockInfoById(di.PRODID, this.inputDocument.DOCMANCLIID, this.inputDocument.DOCCURRID, this.ms, this.is, this.as, new Date()).pipe(

        timeout(10000),
        retry(1),
        catchError(this.handleError('loadStockInfo', [{
          PRODSTOCK: 0, PRODSOLD: 0, PRODRESERVE: 0, PRODONWAY: 0,
          PRODSTOCKM: 0, PRODSTOCKI: 0, PRODSTOCKA: 0
        }])),

        map(stockInfo => {

          di.prodInfo = {

            ...di.prodInfo,

            PRODSTOCK: stockInfo[0].PRODSTOCK,
            PRODSOLD: stockInfo[0].PRODSOLD,
            PRODRESERVE: stockInfo[0].PRODRESERVE,
            PRODONWAY: stockInfo[0].PRODONWAY,

            PRODSTOCKM: stockInfo[0].PRODSTOCKM,
            PRODSTOCKI: stockInfo[0].PRODSTOCKI,
            PRODSTOCKA: stockInfo[0].PRODSTOCKA,
          };

          di.DOCITEMGLOBALSTOCK = di.prodInfo.PRODSTOCK;

          return di;
        }))),

      concatMap(di => this.resolveIncomePriceAsOfDate$(di.PRODID).pipe(
        switchMap(asOfDate => forkJoin({
          priceA: this.prodApi.getProdIncomePriceById(di.PRODID, this.inputDocument.DOCMANCLIID, this.as, this.inputDocument.DOCCURRID, di.DOCITEMQTY, asOfDate).pipe(
            timeout(10000),
            retry(1),
            catchError(this.handleError('loadPriceA', [{ PRODINCOMEPRICE: 0 }])),
            map(price => price?.length > 0 ? price[0].PRODINCOMEPRICE * this.inputDocument.DOCRATEVAL : 0)
          ),
          priceI: this.prodApi.getProdIncomePriceById(di.PRODID, this.inputDocument.DOCMANCLIID, this.is, this.inputDocument.DOCCURRID, di.DOCITEMQTY, asOfDate).pipe(
            timeout(10000),
            retry(1),
            catchError(this.handleError('loadPriceI', [{ PRODINCOMEPRICE: 0 }])),
            map(price => price?.length > 0 ? price[0].PRODINCOMEPRICE * this.inputDocument.DOCRATEVAL : 0)
          ),
          priceM: this.prodApi.getProdIncomePriceById(di.PRODID, this.inputDocument.DOCMANCLIID, this.ms, this.inputDocument.DOCCURRID, di.DOCITEMQTY, asOfDate).pipe(
            timeout(10000),
            retry(1),
            catchError(this.handleError('loadPriceM', [{ PRODINCOMEPRICE: 0 }])),
            map(price => price?.length > 0 ? price[0].PRODINCOMEPRICE * this.inputDocument.DOCRATEVAL : 0)
          )
        }).pipe(
          map(results => {
            di.prodInfo.PRODINPRICEA = results.priceA;
            di.prodInfo.PRODINPRICEI = results.priceI;
            di.prodInfo.PRODINPRICEM = results.priceM;
            return di;
          })
        ))
      )),

      // Below is default hierarchy of stocks
      // Auxilliary (rare)
      // Import (usually)
      // Main
      // Here we will come with real stock price
      // NOTE: actual income price split (covered vs excess qty) is computed in
      // calculateDiRowProfitSync which is always called immediately after this step.
      // This map only populates PRODINPRICEM/I/A — do NOT set DOCINCOMEPRICE here.

      map(di => di),
    );
  }

  // Updated calculateDiRowProfitObservable
  // Refactored to use RxJS operators for better readability and maintainability
  // This method calculates profit for a single DocItems row
  // after pulling stock and income price info

  // calculateDiRowProfitSync: synchronous profit calc using already-loaded prodInfo
  // (populated by applyEnrichedData on initial load, or pullDiRowStockIncomePriceObservable on row edit)
  //
  // Income sum is split into two portions:
  //   coveredQty  = min(qty, totalStock)  → priced at FIFO weighted average of per-depot stock prices
  //   excessQty   = max(0, qty - stock)   → priced at fallback (memory-mode average or PRODINPRICE × rate)
  // DOCINCOMEPRICE = effective per-unit income price = incomeSum / qty  (for display column "Входная")
  /**
   * Computes `DOCINCOMEPRICE`, `DOCITEMPROFIT`, and `DOCITEMPROFITSUM` for a single
   * row **synchronously** using already-loaded `prodInfo` data.
   *
   * ### Income-price algorithm
   * The sold quantity is split into two portions:
   * - **`coveredQty`** = `min(qty, totalStock)` — priced at the FIFO weighted average
   *   of per-depot stock prices (`PRODINPRICEM/I/A × PRODSTOCKM/I/A`).
   * - **`excessQty`** = `max(0, qty − totalStock)` — priced at a fallback:
   *   - In {@link profitMode}: arithmetic mean of non-zero depot prices.
   *   - Otherwise: `PRODINPRICE × DOCRATEVAL`.
   *
   * `DOCINCOMEPRICE = (coveredQty × stockPrice + excessQty × fallback) / qty`
   *
   * When `prodInfo` is absent the legacy formula
   * `DOCITEMPROFIT = salePrice − DOCINCOMEPRICE` is used instead.
   *
   * @param di - The document item row to update (mutated in place and returned).
   * @returns The same `di` reference with profit fields populated.
   */
  private calculateDiRowProfitSync(di: DocItems): DocItems {
    if (di.prodInfo) {
      const qty = di.DOCITEMQTY || 0;
      const totalStock = (di.prodInfo.PRODSTOCKM || 0)
        + (di.prodInfo.PRODSTOCKI || 0)
        + (di.prodInfo.PRODSTOCKA || 0);

      // Per-unit FIFO income price for stock-covered units
      let stockPrice = 0;
      if (totalStock > 0) {
        stockPrice = (
          (di.prodInfo.PRODINPRICEM * (di.prodInfo.PRODSTOCKM || 0)) +
          (di.prodInfo.PRODINPRICEI * (di.prodInfo.PRODSTOCKI || 0)) +
          (di.prodInfo.PRODINPRICEA * (di.prodInfo.PRODSTOCKA || 0))
        ) / totalStock;
      }

      // Fallback price for units that exceed available stock
      let fallbackPrice = 0;
      if (this.profitMode) {
        const nonZero = [di.prodInfo.PRODINPRICEM, di.prodInfo.PRODINPRICEI, di.prodInfo.PRODINPRICEA]
          .filter(p => p > 0);
        fallbackPrice = nonZero.length > 0
          ? nonZero.reduce((a, b) => a + b, 0) / nonZero.length
          : (di.prodInfo.PRODINPRICE ? di.prodInfo.PRODINPRICE * this.inputDocument.DOCRATEVAL : 0);
      } else {
        fallbackPrice = di.prodInfo.PRODINPRICE ? di.prodInfo.PRODINPRICE * this.inputDocument.DOCRATEVAL : 0;
      }

      // Split qty into stock-covered and excess portions
      const coveredQty = Math.min(qty, totalStock);
      const excessQty = Math.max(0, qty - totalStock);
      const incomeSum = (coveredQty * stockPrice) + (excessQty * fallbackPrice);

      // Effective per-unit income price for display ("Входная" column)
      if (qty > 0) {
        di.DOCINCOMEPRICE = incomeSum / qty;
      } else {
        di.DOCINCOMEPRICE = stockPrice > 0 ? stockPrice : fallbackPrice;
      }

      const salePrice = di.U4ETKA || di.DOCITEMPRICE;
      di.DOCITEMPROFITSUM = (salePrice * qty) - incomeSum;
      di.DOCITEMPROFIT = qty > 0 ? di.DOCITEMPROFITSUM / qty : 0;

    } else {
      di.DOCITEMPROFIT = (di.U4ETKA || di.DOCITEMPRICE) - (di.DOCINCOMEPRICE ?? 0);
      di.DOCITEMPROFITSUM = di.DOCITEMQTY * di.DOCITEMPROFIT;
    }

    return di;
  }

  /**
   * Opens the FIFO income-price breakdown popup in multi-depot mode — this component's
   * `DOCINCOMEPRICE` blends up to three depots (Main/Import/Auxiliary) stock-weighted, plus a
   * fallback for any quantity exceeding total stock, unlike `edit-document-content-receipt`'s
   * single-depot price. No-ops if `docItem.prodInfo` hasn't loaded yet (nothing to explain).
   *
   * KEEP IN SYNC with {@link calculateDiRowProfitSync}: this duplicates that method's
   * `coveredQty`/`excessQty`/`stockPrice`/`fallbackPrice` formula rather than having
   * `calculateDiRowProfitSync` expose them, the same tradeoff already accepted for the SQL
   * breakdown function's own duplication of `ufn_getprodincomeprice_iTVF`'s FIFO CTEs (see
   * `SQL-Database-migration/FUNCTIONS/ufn_getprodincomeprice_breakdown_iTVF_deploy.sql`'s
   * header comment) — any change to that formula needs to land here too.
   *
   * A 4th depot ("Виробництво", `this.mfg`) is included purely informationally, for BOM-built
   * products assembled via `new-document-dlg-process-tran-prod` — it is NOT part of the
   * Main/Import/Aux blend above and must never become one. Traced (owner request, 2026-09-03):
   * `ufn_getprodincomeprice_breakdown_iTVF`'s `intake_raw` CTE filters transfer documents by
   * *destination* stock only (`DOCCLIDEPID = @stockid`), never by source, and the manufacturing
   * "return completed product to stock" document always sets `DOCCLIDEPID: mainStockId`
   * regardless of which site built it (`new-document-dlg-process-tran-prod`'s
   * `createNewBackSubscription`) — so a manufactured product's real income price already shows
   * up correctly under "Основний склад" above with no special case needed. This depot instead
   * shows the assembly site's own FIFO history for the SAME product (`stockId: this.mfg`,
   * filtering by `DOCCLIDEPID = mfg` — i.e. what moved INTO the site, matching the "move to
   * production" documents `createA2MoveSubscription` builds) — a sanity/context view, e.g. to
   * confirm nothing is stuck there mid-run. `stock`/`price` are passed `null` (not `0`): there is
   * no equivalent of `PRODSTOCKM/I/A`/`PRODINPRICEM/I/A` for this site from `getProdInfoById`
   * (that endpoint is hardcoded to exactly three stock slots), so there is nothing meaningful to
   * show in the depot header — only the per-batch breakdown table itself.
   *
   * `fd` (the as-of date passed to `openMultiDepot`, and to every per-depot FIFO call the
   * dialog makes) comes from {@link resolveIncomePriceAsOfDate$} — the same date
   * `pullDiRowStockIncomePriceObservable` now uses for the live `PRODINPRICEM/I/A` fetch, so
   * the popup always explains the price the cell actually shows. This makes the method async:
   * the dialog opens once that lookup resolves, not instantly on click.
   */
  openIncomePriceBreakdown(docItem: DocItems): void {
    if (!docItem.prodInfo) {
      return;
    }

    this.resolveIncomePriceAsOfDate$(docItem.PRODID).subscribe(fd => {
      const qty = docItem.DOCITEMQTY || 0;
      const stockM = docItem.prodInfo.PRODSTOCKM || 0;
      const stockI = docItem.prodInfo.PRODSTOCKI || 0;
      const stockA = docItem.prodInfo.PRODSTOCKA || 0;
      const totalStock = stockM + stockI + stockA;

      let stockPrice = 0;
      if (totalStock > 0) {
        stockPrice = (
          ((docItem.prodInfo.PRODINPRICEM || 0) * stockM) +
          ((docItem.prodInfo.PRODINPRICEI || 0) * stockI) +
          ((docItem.prodInfo.PRODINPRICEA || 0) * stockA)
        ) / totalStock;
      }

      let fallbackPrice = 0;
      if (this.profitMode) {
        const nonZero = [docItem.prodInfo.PRODINPRICEM, docItem.prodInfo.PRODINPRICEI, docItem.prodInfo.PRODINPRICEA]
          .filter((p): p is number => !!p && p > 0);
        fallbackPrice = nonZero.length > 0
          ? nonZero.reduce((a, b) => a + b, 0) / nonZero.length
          : (docItem.prodInfo.PRODINPRICE ? docItem.prodInfo.PRODINPRICE * this.inputDocument.DOCRATEVAL : 0);
      } else {
        fallbackPrice = docItem.prodInfo.PRODINPRICE ? docItem.prodInfo.PRODINPRICE * this.inputDocument.DOCRATEVAL : 0;
      }

      this.priceBreakdownDialog.openMultiDepot({
        prodId: docItem.PRODID,
        cm: this.inputDocument.DOCMANCLIID,
        cr: this.inputDocument.DOCCURRID,
        qty,
        fd,
        depots: [
          { label: 'Основний склад', stockId: this.ms, stock: stockM, price: docItem.prodInfo.PRODINPRICEM || 0 },
          { label: 'Імпортний склад', stockId: this.is, stock: stockI, price: docItem.prodInfo.PRODINPRICEI || 0 },
          { label: 'Додатковий склад', stockId: this.as, stock: stockA, price: docItem.prodInfo.PRODINPRICEA || 0 },
          { label: 'Виробництво', stockId: this.mfg, stock: null, price: null },
        ],
        coveredQty: Math.min(qty, totalStock),
        excessQty: Math.max(0, qty - totalStock),
        stockPrice,
        fallbackPrice,
        cellPriceMain: docItem.DOCINCOMEPRICE ?? null,
      });
    });
  }

  /**
   * Async wrapper for profit calculation that first refreshes stock / income-price
   * data from the server before running the synchronous math.
   *
   * Used by {@link fastReloadDocContent} and {@link onProductSelection} where the
   * income price may have changed since the last full load (e.g. after a qty edit).
   *
   * Sequence:
   * 1. {@link pullDiRowStockIncomePriceObservable} — fetches latest depot stock + prices.
   * 2. {@link calculateDiRowProfitSync} — computes FIFO-weighted income price and profit.
   *
   * @param di - The document item row to update.
   * @returns An observable that emits the updated `di`.
   */
  calculateDiRowProfitObservable(di: DocItems): Observable<DocItems> {
    // Used by fastReloadDocContent and onProductSelection (single-row refresh with HTTP)
    return this.pullDiRowStockIncomePriceObservable(di).pipe(
      map(di => this.calculateDiRowProfitSync(di))
    );
  }

  // Updated calculateDiRowStatusObservable
  // Refactored to use RxJS operators for better readability and maintainability
  // This method calculates status flags for a single DocItems row
  // after profit calculation
  // and updates necessary fields

  /**
   * Calculates all boolean status / highlight flags for a single document-item row
   * and sets the composite `hlt` flag used to colour the row in the table.
   *
   * Flags set (DOCTYPE 1 / 12 only):
   * | Flag  | Meaning |
   * |-------|---------|
   * | `bip` | Sale price below income price |
   * | `bmd` | Discount exceeds maximum (dial) discount |
   * | `dmi` | Discount in base tier |
   * | `dme` | Discount in qty tier |
   * | `dma` | Discount in dial tier |
   * | `cnm` | Currency mismatch with document |
   * | `rnm` | Exchange-rate mismatch with document |
   * | `sha` | Row fully shipped |
   * | `mtc` | Customs code missing |
   * | `nta` | Customs code unconfirmed |
   * | `npc` | Product category not set |
   * | `cpn` | Currency price inconsistency |
   * | `lpf` | Negative profit (potential loss) |
   * | `mst` | Remaining qty exceeds available stock |
   * | `hlt` | Any of the above — triggers row highlight |
   *
   * Calls {@link calculateDiRowSum} first so sums are always up to date.
   *
   * @param di - The document item row to evaluate (mutated in place and returned).
   * @returns An observable that emits the updated `di`.
   */
  calculateDiRowStatusObservable(di: DocItems): Observable<DocItems> {
    // environment.production || console.log

    return of(this.calculateDiRowSum(di)).pipe(

      map(di => {

        switch (di.DOCTYPE) {
          case 1:
          case 12:

            // Retail price - looks like already there in pullDiRowPriceAndStockObservable
            //if (di.prodInfo) {
            //  di.DOCITEMRETVPRICE = di.prodInfo.PRODPRICE;
            //  di.DOCITEMRETPRICE = di.DOCITEMRETVPRICE * this.inputDocument.DOCRATEVAL;
            //}

            // Recalculate discount and status flags after profit calc
            di.DISCOUNT = round2((1 - (di.DOCITEMVPRICE / (di.DOCITEMRETVPRICE))) * 100);

            di.cnm = this.inputDocument && (di.DOCITEMCURRID != this.inputDocument.DOCCURRID);
            di.rnm = this.inputDocument && (di.DOCITEMCRATE != this.inputDocument.DOCRATEVAL);
            di.sha = (di.DOCITEMSHIPPED == di.DOCITEMQTY);
            di.mtc = ((di.PRODCUSTCODE || '') == '');

            di.bmd = di.prodInfo && (di.DISCOUNT > round2(di.prodInfo.PRODDIALDISC * 1.01));
            di.dmi = di.prodInfo && (di.DISCOUNT > 0) && (round2(di.DISCOUNT) <= round2(di.prodInfo.PRODBASEDISC * 1.01));
            di.dme = di.prodInfo && (di.DISCOUNT > round2(di.prodInfo.PRODBASEDISC)) && (di.DISCOUNT <= round2(di.prodInfo.PRODQTYDISC * 1.01));
            di.dma = di.prodInfo && (di.DISCOUNT > round2(di.prodInfo.PRODQTYDISC)) && (di.DISCOUNT <= round2(di.prodInfo.PRODDIALDISC * 1.01));
            di.cpn = round2(di.DOCITEMVPRICE * this.inputDocument.DOCRATEVAL) != round2(di.DOCITEMPRICE);
            // TODO:            di.npc = (di.prodInfo?.PRODCATID ?? 0) == 0;
            di.lpf = (di.DOCITEMPROFIT < 0);

            // di.prodInfo.PRODINPRICE is documented as "base income price (foreign currency)" —
            // comparing it directly against DOCITEMVPRICE (document currency) with no DOCRATEVAL
            // conversion mixes currencies. DOCINCOMEPRICE is the FIFO-blended, already-converted
            // income price ("These numbers are in main currency!", document.ts) — the same value
            // shown in the "Структура вхідної ціни" breakdown dialog — so compare it against
            // DOCITEMPRICE (also main currency) instead.
            di.bip = di.prodInfo && (di.DOCINCOMEPRICE != null) && (round2(di.DOCITEMPRICE) < round2(di.DOCINCOMEPRICE));
            di.mst = di.prodInfo && ((di.DOCITEMQTY - di.DOCITEMSHIPPED) > di.prodInfo.PRODSTOCK);
            di.cpn = round1(di.DOCITEMVPRICE * this.inputDocument.DOCRATEVAL) != round1(di.DOCITEMPRICE);
            di.hlt = (di.bip || di.bmd || di.cnm || di.rnm || di.mtc || di.npc || di.nta || di.lpf || di.cpn);

          default:
            break;
        }
        return di;
      }),
    );
  }

  // Helper method to recalculate prices based on changed fields
  // changedFields indicate which fields were modified
  // and need to trigger recalculation of dependent prices
  // For example, if 'price' was changed, we need to recalculate 'vprice' and 'priceNoVat'
  // Also, after recalculation, we need to update the row status Having the stock and income price already loaded

  /**
   * Returns `true` when at least one of the changed fields requires a fresh
   * income-price HTTP call (currently only `'qty'`).
   *
   * Used by {@link recalculatePrices} to choose between the fast synchronous path
   * and the async path that calls {@link calculateDiRowProfitObservable}.
   *
   * @param changedFields - The fields that were modified by the user.
   */
  private requiresIncomePriceFetch(changedFields: PriceField[]): boolean {
    return changedFields.includes('qty');
  }

  /**
   * Recalculates interdependent price fields after the user edits a cell, then
   * triggers profit and status recalculation.
   *
   * **Supported `changedFields` values and their effects:**
   * - `'vprice'`    — derives `DOCITEMPRICEWOVAT` and `DOCITEMPRICE` from `DOCITEMVPRICE`.
   * - `'price'`     — derives `DOCITEMPRICEWOVAT` and `DOCITEMVPRICE` from `DOCITEMPRICE`.
   * - `'priceNoVat'`— derives `DOCITEMPRICE` and `DOCITEMVPRICE` from `DOCITEMPRICEWOVAT`.
   * - `'discount'`  — computes all price fields from `DISCOUNT` and `prodInfo.PRODPRICE`.
   * - `'u4etka'` / `'qty'` — no price recalculation; only downstream profit/status.
   *
   * Chooses one of two downstream paths via {@link requiresIncomePriceFetch}:
   * - **Synchronous** (`price`, `discount`, `u4etka`): calls {@link calculateDiRowProfitSync}
   *   inline, then subscribes to {@link calculateDiRowStatusObservable} +
   *   {@link calculateDocumentSummaryObservable} without a spinner.
   * - **Async** (`qty`): cancels any pending recalc subscription for this row,
   *   shows the spinner, and runs the full
   *   {@link calculateDiRowProfitObservable} → status → summary chain.
   *
   * @param di            - The document item row being edited (mutated in place).
   * @param changedFields - One or more fields that changed.
   */
  private recalculatePrices(di: DocItems, changedFields: PriceField[]) {
    const vatMultiplier = (100.0 + di.PRODVAT) / 100.0;

    for (const changedField of changedFields) {
      switch (changedField) {
        case 'vprice':
          di.DOCITEMPRICEWOVAT = round1((di.DOCITEMVPRICE * this.inputDocument.DOCRATEVAL) / vatMultiplier);
          di.DOCITEMPRICE = round2(di.DOCITEMPRICEWOVAT * vatMultiplier);
          break;
        case 'price':
          di.DOCITEMPRICEWOVAT = round1(di.DOCITEMPRICE / vatMultiplier);
          di.DOCITEMPRICE = round2(di.DOCITEMPRICEWOVAT * vatMultiplier);
          di.DOCITEMVPRICE = di.DOCITEMPRICE / this.inputDocument.DOCRATEVAL;
          break;
        case 'priceNoVat':
          di.DOCITEMPRICE = round2(di.DOCITEMPRICEWOVAT * vatMultiplier);
          di.DOCITEMVPRICE = di.DOCITEMPRICE / this.inputDocument.DOCRATEVAL;
          break;
        case 'discount':
          di.DOCITEMPRICEWOVAT = round1(round2(di.prodInfo.PRODPRICE * ((100 - round1(di.DISCOUNT)) / 100) * this.inputDocument.DOCRATEVAL) / vatMultiplier);
          di.DOCITEMPRICE = round2(di.DOCITEMPRICEWOVAT * vatMultiplier);
          di.DOCITEMVPRICE = di.DOCITEMPRICE / this.inputDocument.DOCRATEVAL;
          break;
        case 'u4etka':
        case 'qty':
          break;
      }
    }

    if (!this.requiresIncomePriceFetch(changedFields)) {
      // ── Synchronous path: price / discount / U4ETKA — no HTTP, no spinner ─────
      this.calculateDiRowProfitSync(di);
      this.calculateDiRowSum(di);
      this.addSubscription(
        this.calculateDiRowStatusObservable(di).pipe(
          concatMap(() => this.calculateDocumentSummaryObservable())
        ).subscribe()
      );
      return;
    }

    // ── Async path: qty changed — income price depends on stock × qty ────────────
    // Cancel any pending recalc for this row to prevent subscription stacking.
    this.recalcSubscriptions.get(di.DOCITEMID)?.unsubscribe();

    this.contentIsLoading = true;
    this.contentLoading.emit(this.contentIsLoading);

    const sub = of(di).pipe(
      concatMap(di => this.calculateDiRowProfitObservable(di)),
      concatMap(di => this.calculateDiRowStatusObservable(di)),
      concatMap(() => this.calculateDocumentSummaryObservable()),
      finalize(() => {
        this.recalcSubscriptions.delete(di.DOCITEMID);
        this.contentIsLoading = false;
        this.contentLoading.emit(this.contentIsLoading);
      })
    ).subscribe();

    this.recalcSubscriptions.set(di.DOCITEMID, sub);
    this.addSubscription(sub);
  }

  /**
   * Calculates the four row-level monetary totals and stores them on `di`:
   * - `DOCITEMSUMWOVAT` = `qty × DOCITEMPRICEWOVAT`
   * - `DOCITEMSUM`      = `qty × DOCITEMPRICE`
   * - `DOCITEMVSUM`     = `qty × DOCITEMVPRICE`
   * - `DOCITEMSUMU4`    = `qty × U4ETKA`
   *
   * Pure synchronous helper — no HTTP calls, no side-effects beyond `di`.
   *
   * @param di - The document item row to update (mutated in place and returned).
   * @returns The same `di` reference.
   */
  calculateDiRowSum(di: DocItems): DocItems {
    //    console.log('*** EditDocumentContentComponent calculateDiRowSum: ' + di.DOCITEMID);

    di.DOCITEMSUMWOVAT = di.DOCITEMQTY * di.DOCITEMPRICEWOVAT;
    di.DOCITEMSUM = di.DOCITEMQTY * di.DOCITEMPRICE;
    di.DOCITEMVSUM = di.DOCITEMQTY * di.DOCITEMVPRICE;
    di.DOCITEMSUMU4 = di.DOCITEMQTY * di.U4ETKA;

    return di;
  }

  //calculateDiRowStatus(di: DocItems): DocItems {
  //  //console.log('*** EditDocumentContentComponent calculateDiRowStatus: ' + di.DOCITEMID);

  //  const sub = of(this.calculateDiRowSum(di)).pipe(
  //    map(di => {
  //      // Profit
  //      di.DOCITEMPROFIT = (di.U4ETKA || di.DOCITEMPRICE) - (di.DOCINCOMEPRICE ?? 0);
  //      di.DOCITEMPROFITSUM = di.DOCITEMQTY * di.DOCITEMPROFIT;
  //      return di;
  //    })

  //    ).subscribe();

  //  switch (di.DOCTYPE) {

  //    case 1:
  //    case 12:

  //      // Trigger profit calculation
  //      // Here is the problem of execution sequence
  //      // below procedure asynchronous, but we need result here synchronously
  //      // So we would createtwo async procedure: one to calculate profit and one to calculate row status after profit calc

  //      // Also calculateDocumentSummary would be called after profit calc in async way
  //      // And we need to change this.calcDocSum$.next(this.inputDocument) to Observable

  //      this.calcDocItemProfit$.next(di);

  //      di.DISCOUNT = round2((1 - (di.DOCITEMVPRICE / (di.prodInfo.PRODPRICE))) * 100);

  //      di.DOCITEMRETPRICE = di.prodInfo.PRODPRICE * this.inputDocument.DOCRATEVAL;
  //      di.DOCITEMRETVPRICE = di.prodInfo.PRODPRICE;

  //      di.cnm = this.inputDocument && (di.DOCITEMCURRID != this.inputDocument.DOCCURRID);
  //      di.rnm = this.inputDocument && (di.DOCITEMCRATE != this.inputDocument.DOCRATEVAL);
  //      di.sha = (di.DOCITEMSHIPPED == di.DOCITEMQTY);
  //      di.mtc = ((di.PRODCUSTCODE || '') == '');

  //      di.bmd = di.prodInfo && (di.DISCOUNT > round2(di.prodInfo.PRODDIALDISC * 1.01));  // Leave some room for round error
  //      di.dmi = di.prodInfo && (di.DISCOUNT > 0) && (round2(di.DISCOUNT) <= round2(di.prodInfo.PRODBASEDISC * 1.01));
  //      di.dme = di.prodInfo && (di.DISCOUNT > round2(di.prodInfo.PRODBASEDISC)) && (di.DISCOUNT <= round2(di.prodInfo.PRODQTYDISC * 1.01));
  //      di.dma = di.prodInfo && (di.DISCOUNT > round2(di.prodInfo.PRODQTYDISC)) && (di.DISCOUNT <= round2(di.prodInfo.PRODDIALDISC * 1.01));
  //      di.cpn = round2(di.DOCITEMVPRICE * this.inputDocument.DOCRATEVAL) != round2(di.DOCITEMPRICE);
  //      break;

  //    case 8:
  //    case 9:
  //    case 11:
  //      break;

  //    default:
  //      break;
  //  }
  //  // this.calcDocSum$.next(this.inputDocument)
  //  this.calculateDocumentSummary();

  //  return di;
  //}

  // Updated calculateDocItemsFields
  //calculateDocItemsFields(data: DocItems[]) {
  //  this.contentIsLoading = true;
  //  this.contentLoading.emit(this.contentIsLoading);

  //  this.docItems = [...data];

  //  this.diSumQuery =
  //    this.reassignSubscription(
  //      this.diSumQuery,
  //      from(this.docItems).pipe(
  //        map(di => {
  //          di.cloneIndex = 0;
  //          di.PRODVAT = (di.PRODVAT ?? this.globalSettinsData.vatValue);
  //          di.DOCITEMPRICEWOVAT = round1(di.DOCITEMPRICE / ((100.0 + di.PRODVAT) / 100.0));
  //          di.DOCITEMPRICE = round2(di.DOCITEMPRICEWOVAT * ((100.0 + di.PRODVAT) / 100.0));

  //          di.DOCITEMSUMWOVAT = di.DOCITEMQTY * di.DOCITEMPRICEWOVAT;
  //          di.DOCITEMSUM = di.DOCITEMSUMWOVAT * ((100.0 + di.PRODVAT) / 100.0);

  //          return di;
  //        }),

  //        concatMap(di => this.pullDiRowPriceAndStockObservable(di)),

  //        toArray(),

  //        // ... rest of the pipeline
  //        finalize(() => {
  //          environment.production || console.log('*** EditDocumentContentComponent calculateDocItemsFields => finalize(docItems)');
  //          this.inputDocument.items = this.docItems;
  //          this.calcDocProfit$.next(this.inputDocument.items);
  //          this.saveDiCopy();
  //          this.contentIsLoading = false;
  //          this.contentLoading.emit(this.contentIsLoading);
  //        })
  //      ).subscribe()
  //    );
  //}

  // Updated calculateDocumentSummary
  // THis procedure called in the end of calculateDiRowStatus
  // Have to check wether calculation the same as in Observable version below
  // Or better to call observable method to keep math and logic in single place

  //calculateDocumentSummary() {

  //  // this.calcDocSum$.next(this.inputDocument);

  //  this.addSubscription(
  //    this.calculateDocumentSummaryObservable().subscribe()
  //  );

  //  // Or even better to use reassignSubscription approach?
  //}

  /**
   * Recomputes document-level aggregate fields and emits the document health status.
   *
   * Aggregates calculated:
   * - `DOCITEMS`       — count of non-zero-qty rows (respects {@link countEmpty}).
   * - `DOCTOTALITEMS`  — sum of all `DOCITEMQTY` values.
   * - `DOCSUMU4ETKA`   — sum of `qty × U4ETKA`.
   * - `DOCVSUM`        — sum of `qty × DOCITEMVPRICE`.
   * - `DOCSUMWOVAT`    — sum of `DOCITEMSUMWOVAT`.
   * - `DOCVAT`         — sum of `DOCITEMSUM − DOCITEMSUMWOVAT`.
   * - `DOCSUM`         — sum of `DOCITEMSUM`.
   * - `DOCSUMPROFIT`   — sum of `DOCITEMPROFITSUM`.
   *
   * After aggregation it iterates `docItems` to derive `documentItemsStatus`
   * ({@link DocumentStatusFlag}) and emits the result via {@link getDocumentStatus}.
   *
   * Uses `debounceTime(500)` so rapid successive calls are coalesced.
   *
   * @returns An observable that completes after updating the document and emitting status.
   */
  calculateDocumentSummaryObservable() {

    return of(this.inputDocument).pipe(
      debounceTime(500),
      filter(doc => doc !== null),

      switchMap(doc => doc ? of(doc) : EMPTY),
      catchError(error => {
        environment.production || console.error('calculateDocumentSummaryObservable error:', error);
        return EMPTY;
      }),

      switchMap(doc => {  // ✅ Change to switchMap
        // console.log('*** EditDocumentContentComponent calculateDocumentSummaryObservable start: ');

        // Calculate document-level summaries
        doc.DOCITEMS = (this.docItems || [])
          //          .filter(e => this.countEmpty || (e.DOCITEMQTY && e.DOCITEMQTY !== 0))
          //          .filter(e => this.countEmpty || e.DOCITEMQTY)
          .filter(e => this.countEmpty || (e.DOCITEMQTY != null && e.DOCITEMQTY !== 0))
          .length;

        doc.DOCTOTALITEMS = this.docItems
          .filter(e => this.countEmpty || (e.DOCITEMQTY != null && e.DOCITEMQTY !== 0))
          .reduce((r, di) => r + di.DOCITEMQTY, 0);

        doc.DOCSUMU4ETKA = this.docItems.reduce((r, di) => r + di.DOCITEMQTY * di.U4ETKA, 0);
        doc.DOCVSUM = this.docItems.reduce((r, di) => r + di.DOCITEMQTY * di.DOCITEMVPRICE, 0);
        doc.DOCSUMWOVAT = this.docItems.reduce((r, di) => r + di.DOCITEMSUMWOVAT, 0);
        doc.DOCVAT = this.docItems.reduce((r, di) => r + (di.DOCITEMSUM - di.DOCITEMSUMWOVAT), 0);
        doc.DOCSUM = this.docItems.reduce((r, di) => r + di.DOCITEMSUM, 0);
        doc.DOCSUMPROFIT = this.docItems.reduce((r, di) => r + di.DOCITEMPROFITSUM, 0);

        this.documentItemsStatus = 0;

        environment.production || console.log('*** EditDocumentContentComponent in calculateDocumentSummaryObservable: ' + JSON.stringify({
          IT: doc.DOCITEMS,
          TI: doc.DOCTOTALITEMS,
          VS: doc.DOCVSUM,
          SWV: doc.DOCSUMWOVAT,
          V: doc.DOCVAT,
          S: doc.DOCSUM,
          SP: doc.DOCSUMPROFIT
        }));

        // Now evaluate document status based on item flags
        return from(this.docItems).pipe(
          map(di => {
            if (di.hlt) {
              if (di.bip || di.cnm || di.rnm || di.mtc || di.lpf) {
                this.documentItemsStatus = DocumentStatusFlag.SeriousError.value;
              }
              if (this.documentItemsStatus < DocumentStatusFlag.SeriousError.value) {
                if (di.cnm || di.rnm || di.mtc || di.lpf) {
                  this.documentItemsStatus = DocumentStatusFlag.MinorIssue.value;
                }
              }
            }
          }),
          toArray(),

          finalize(() => {
            this.getDocumentStatus.emit(this.documentItemsStatus);
          })
        );
      })
    );
  }

  /** Persists column-visibility state to localStorage, then mass-unsubscribes all tracked subscriptions and completes `destroy$`. */
  ngOnDestroy(): void {
    console.log('*** EditDocumentContentComponent ngOnDestroy: ')

    if (!this.setShowRowOptions) {
      localStorage.setItem(this.storeNameShowRowOptions, JSON.stringify(this.showRowOptions));
    }
    localStorage.setItem(this.storeNameShowRowOptions + '-columns-filter-state', JSON.stringify(this.showFilterValue));

    this.activeSubscriptions.forEach(sub => sub?.unsubscribe());
    this.activeSubscriptions = [];

    this.recalcSubscriptions.forEach(sub => sub?.unsubscribe());
    this.recalcSubscriptions.clear();

    this.destroy$.next();
    this.destroy$.complete();

    if (this.docItemQuery) { this.docItemQuery.unsubscribe(); }
    if (this._prodShippedQuery) { this._prodShippedQuery.unsubscribe(); }
    if (this.diSaveQuery) { this.diSaveQuery.unsubscribe(); }
    if (this.diSumQuery) { this.diSumQuery.unsubscribe(); }
    if (this.diGroupQuery) { this.diGroupQuery.unsubscribe(); }
    //  if (this.calcDocSumSubs) { this.calcDocSumSubs.unsubscribe(); }
    //  if (this.calcDocProfitSubs) { this.calcDocProfitSubs.unsubscribe(); }
    //  if (this.calcDocItemProfitSubs) { this.calcDocItemProfitSubs.unsubscribe(); }
  }

  /** Reacts to `setShowRowOptions` changes (syncs column visibility) and `inputDocument` changes (triggers full reload). */
  ngOnChanges(changes: SimpleChanges): void {
    //    console.log('*** EditDocumentContentComponent ngOnDestroy: ')
    for (let propName in changes) {
      let change = changes[propName];

      console.log('*** EditDocumentContentComponent ngOnChanges ' + propName + ' (actual): ' + JSON.stringify(change.currentValue));
      //      console.log('*** EditDocumentContentComponent ngOnChanges ' + propName + ' (was):    ' + JSON.stringify(change.previousValue));

      switch (propName) {
        case 'setShowRowOptions':
          this.showRowOptions = [...change.currentValue];
          break;

        //case 'knockKnock':
        //  this.docItemList$.next(this.inputDocument.DOCID);
        //  break;

        case 'inputDocument':

          if (change.currentValue && !change.firstChange) {
            this.docItemList$.next(change.currentValue.DOCID);
          }

          // Refetch on every change (including the first) -- unlike the DOCID-driven
          // reload above, the customer can change on an already-loaded document without
          // DOCID changing, and onProductSelection() needs a tier in place before the
          // very first row is ever priced.
          this.refreshCustomerTier(change.currentValue as Document);
          break;
      }
    }
  }

  /** Registers custom PrimeNG filter strategies, reads global settings, resolves depot IDs, and restores stored column visibility. */
  ngOnInit(): void {
    console.log('*** EditDocumentContentComponent ngOnInit: ')

    this.filterService.register('gtOrZero', (value: number, filter: number) => (
      ((filter == 0) && (value == 0)) || ((filter > 0) && (value > filter))
    )
    );

    this.filterService.register('GteOrZero', (value: number, filter: number) => (
      ((filter == 0) && (value == 0)) || ((filter > 0) && (value >= filter))
    )
    );

    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    this.ms = this.globalSettinsData.mainStockId;
    this.is = this.globalSettinsData.impCountFlag ? this.globalSettinsData.impStockId : 0;
    this.as = this.globalSettinsData.auxCountFlag ? this.globalSettinsData.auxStockId : 0;
    this.mfg = this.globalSettinsData.manDepartmentId || 0;

    if (!this.setShowRowOptions) {
      let _storedShowRowOptions: string = localStorage.getItem(this.storeNameShowRowOptions);
      if (_storedShowRowOptions) { this.showRowOptions = JSON.parse(_storedShowRowOptions); }
    }

    let _storedFilterOptions: string = localStorage.getItem(this.storeNameShowRowOptions + '-columns-filter-state');
    if (_storedFilterOptions) { this.showFilterValue = JSON.parse(_storedFilterOptions); }

    this.qtyChange$.pipe(
      debounceTime(600),
      takeUntil(this.destroy$)
    ).subscribe(di => this.recalculatePrices(di, ['qty']));

    //this.reloadDocContent(null);
  }

  onRowsOptionChange(event) {
  }

  /**
   * Handles product selection from the inline autocomplete within a row edit.
   *
   * Copies identifying fields from the selected product onto `di`, then runs the
   * full single-row enrichment pipeline:
   * 1. `getProdInfoById` — base price for the selected product.
   * 2. {@link pullDiRowPriceAndStockObservable} — retail price, discount options, shipped qty.
   * 3. {@link calculateDiRowProfitObservable} — stock-weighted income price and profit.
   * 4. {@link calculateDiRowStatusObservable} — status / highlight flags.
   * 5. {@link calculateDocumentSummaryObservable} — document totals.
   *
   * Updates `docItems[idx]` with a deep copy of the enriched row.
   *
   * @param event - The product selected by the user.
   * @param di    - The document item row being edited.
   */
  onProductSelection(event: Product, di: DocItems) {
    console.log('*** EditDocumentContentComponent onProductSelection: ' + JSON.stringify(event));

    di.PRODID = event.PRODID;
    di.PRODCODE = event.PRODCODE;
    di.PRODVAT = event.PRODVAT;
    di.PRODCUSTCODE = event.PRODCUSTCODE;

    // ✅ Properly manage subscription
    this._prodShippedQuery =
      this.reassignSubscription(
        this._prodShippedQuery,
        this.prodApi.getProdInfoById(
          di.PRODID,
          this.inputDocument.DOCMANCLIID,
          this.inputDocument.DOCCURRID,
          this.inputDocument.DOCMANDEPID,
          0, 0,
          new Date()
        ).pipe(
          timeout(10000),
          retry(1),
          catchError(this.handleError('loadProdInfo', [])),

          filter(data => data && data.length > 0),

          map(data => {

            di.prodInfo = data[0];

            switch (di.DOCTYPE) {
              case 1:
              case 12:
                di.PRODVAT = (di.PRODVAT ?? this.globalSettinsData.vatValue);

                // A row's own DISCOUNT (hand-typed, or picked from buildDiscountOptionsMenu
                // on an earlier product) is always respected first. Only an unset DISCOUNT --
                // true for every brand-new row, per onAddRow()'s blank starting DocItems --
                // falls back to the customer's tier-entitled percentage (2026-09-21) instead
                // of the old flat `0` (catalogue retail regardless of tier). See
                // `customerTier`'s own doc comment for why this is fetched once per document,
                // not per row.
                di.DISCOUNT = di.DISCOUNT ?? getTierDiscountPercent(this.customerTier, di.prodInfo);

                di.DOCITEMPRICEWOVAT = round1(round2(di.prodInfo.PRODPRICE * ((100 - di.DISCOUNT) / 100) * this.inputDocument.DOCRATEVAL) / ((100.0 + di.PRODVAT) / 100.0));

                di.DOCITEMPRICE = round2(di.DOCITEMPRICEWOVAT * ((100.0 + di.PRODVAT) / 100.0));
                di.DOCITEMVPRICE = di.DOCITEMPRICE / this.inputDocument.DOCRATEVAL;

                di.DOCITEMPRODNAME = ((event.PRODNOTE || '').trim() != '') ? event.PRODNOTE.trim() : event.PRODNAME.trim();
                break;

              default:
                di.DOCITEMPRODNAME = event.PRODNAME;
                di.DOCITEMPRICEWOVAT = (data[0].PRODPRICE * this.inputDocument.DOCRATEVAL) / ((100.0 + di.PRODVAT) / 100.0);
                di.DOCITEMPRICE = round2(data[0].PRODPRICE * this.inputDocument.DOCRATEVAL);
                di.DOCITEMVPRICE = di.DOCITEMPRICE / this.inputDocument.DOCRATEVAL;
                break;
            }

            this.buildDiscountOptionsMenu(di);
            return di;
          }),
          // ✅ Load complete product info with stock data - RETURNS di
          concatMap(di => this.pullDiRowPriceAndStockObservable(di)),
          // ✅ Calculate profit (which internally calls pullDiRowStockIncomePriceObservable) - RETURNS di
          concatMap(di => this.calculateDiRowProfitObservable(di)),
          // ✅ Update status flags - RETURNS di
          concatMap(di => this.calculateDiRowStatusObservable(di)),
          // ✅ CRITICAL FIX: Update the docItems array with the modified di
          tap(updatedDi => {
            const idx = this.docItems.findIndex(d => d.DOCITEMID === updatedDi.DOCITEMID);
            if (idx >= 0) {
              // Deep copy to ensure all nested objects are updated
              this.docItems[idx] = {
                ...updatedDi,
                prodInfo: updatedDi.prodInfo ? { ...updatedDi.prodInfo } : null
              };
              environment.production || console.log('*** onProductSelection: Updated docItems[' + idx + '] with DOCINCOMEPRICE=' + updatedDi.DOCINCOMEPRICE + ', PROFIT=' + updatedDi.DOCITEMPROFIT);
            }
          }),

          catchError(error => {
            environment.production || console.error('onProductSelection error:', error);
            return EMPTY;
          }),

          // ✅ Recalculate document summary
          concatMap(() => this.calculateDocumentSummaryObservable()),

          finalize(() => {
          })
        ).subscribe()
      );
  }

  /**
   * Persists a row edit to the server (POST for new rows, PUT for existing ones),
   * then triggers a fast single-row refresh via {@link fastReloadDocContent}.
   *
   * New rows are identified by `DOCITEMID === -2`; the server-assigned ID is written
   * back to `esg.DOCITEMID` from the response.  The clone entry is removed from
   * {@link docItemsClone} and `editMode` is cleared.
   *
   * @param esg - The document item row that was saved by the user.
   */
  onDiRowEditSave(esg: DocItems) {
    console.log('*** EditDocumentContentComponent in onDiRowEditSave: ');

    let ppid = this.docItemsClone.findIndex(sg => sg.cloneIndex == esg.cloneIndex);
    esg.DOCITEMVPRICE = esg.DOCITEMPRICE / esg.DOCITEMCRATE;

    const apiCall = (esg.DOCITEMID == -2)
      ? this.docApi.postDocItemById(esg)
      : this.docApi.putDocItemById(esg);

    // ✅ Properly manage subscription
    this.diSaveQuery = this.reassignSubscription(
      this.diSaveQuery,
      apiCall.pipe(
        map(result => {
          environment.production || console.log('*** EditDocumentContentComponent save result: ' + JSON.stringify(result));

          this.docItemsClone.splice(ppid, 1);

          if (esg.DOCITEMID == -2) {
            esg.DOCITEMID = result['DOCITEMID'];
          }

          esg.cloneIndex = 0;
          this.fastReloadDocContent(esg);

          this.editMode = false;
          this.editing.emit(this.editMode);
        })
      ).subscribe()
    );
  }

  // Updated bulk operations
  updU4etkaPrice(event) {
    console.log('*** EditDocumentContentComponent in updU4etkaPrice: ' + JSON.stringify(event));

    this.diGroupQuery =
      this.reassignSubscription(
        this.diGroupQuery,

        from(this.docItems).pipe(
          map(di => {
            di.U4ETKA = di.DOCITEMPRICE / this.u4etkaMultiplier;

            //  this.calculateDiRowStatus(di);
            //  this.calculateRowProfit(di);

            return di;
          }),

          // Here is the question - do we need pull the income price again?
          // If we assume that only U4ETKA changes, it is natural to assume
          // that income price remains the same and already calculated

          concatMap(di => this.calculateDiRowProfitObservable(di)),
          concatMap(di => this.calculateDiRowStatusObservable(di)),

          toArray(),

          finalize(() => {
            this.allDocItemsChanged = true;
            this.editing.emit(this.allDocItemsChanged);
          })
        ).subscribe()
      );
  }

  onAddRowOnTop(event) {
    console.log('*** EditDocumentContentComponent in onAddRowOnTop: ' + JSON.stringify(event));

    let minRowIndex = 1000;

    this.docItems.forEach(pp => { minRowIndex = Math.min(minRowIndex, pp.PAYITEMCLIID) });

    let newDi: DocItems = {

      DOCITEMID: -2,
      DOCID: this.inputDocument.DOCID,
      DOCTYPE: this.inputDocument.DOCTYPE,
      PRODID: null,
      DOCITEMPRODNAME: '',
      DOCITEMPRICE: 0,
      DOCITEMQTY: 1,
      DOCITEMVPRICE: 0,
      DOCITEMCURRID: this.inputDocument.DOCCURRID,
      DOCITEMCRATE: this.inputDocument.DOCRATEVAL,
      PAYITEMCLIID: minRowIndex - 5,
      PAYITEMDEPID: null,
      DOCITEMTEXT1: '',
      DOCITEMTEXT2: 'Нет',
      CURCODE: '',
      U4ETKA: null,
      PRODVAT: this.globalSettinsData.vatValue,
      cloneIndex: 0
    };

    this.docItems = [newDi, ...this.docItems];

    let ppe: DocItems = this.docItems.find(e => e.DOCITEMID == -2);
    this.onDiRowEditInit(ppe);
    this.docItemTable.initRowEdit(ppe);
  }

  onAddRow(event) {
    console.log('*** EditDocumentContentComponent in onAddRow: ' + JSON.stringify(event));

    let maxRowIndex = 0;

    this.docItems.forEach(pp => { maxRowIndex = Math.max(maxRowIndex, pp.PAYITEMCLIID) });

    let newDi: DocItems = {

      DOCITEMID: -2,
      DOCID: this.inputDocument.DOCID,
      DOCTYPE: this.inputDocument.DOCTYPE,
      PRODID: null,
      DOCITEMPRODNAME: '',
      DOCITEMPRICE: 0,
      DOCITEMQTY: 1,
      DOCITEMVPRICE: 0,
      DOCITEMCURRID: this.inputDocument.DOCCURRID,
      DOCITEMCRATE: this.inputDocument.DOCRATEVAL,
      PAYITEMCLIID: maxRowIndex + 10,
      PAYITEMDEPID: null,
      DOCITEMTEXT1: '',
      DOCITEMTEXT2: 'Нет',
      CURCODE: '',
      U4ETKA: null,
      PRODVAT: this.globalSettinsData.vatValue,
      cloneIndex: 0
    };

    this.docItems = [...this.docItems, newDi];

    let ppe: DocItems = this.docItems.find(e => e.DOCITEMID == -2);
    this.onDiRowEditInit(ppe);
    this.docItemTable.initRowEdit(ppe);
  }

  onAddRowAbove(event) {
    console.log('*** EditDocumentContentComponent in onAddRowAbove: ' + JSON.stringify(event));

    // Insert newDi into docItems right after the selected sDI (i.e., after its index)
    if (this.sDI) {
      const selectedIndex = this.docItems.findIndex(item => item.DOCITEMID === this.sDI.DOCITEMID);

      let newDi: DocItems = {
        DOCITEMID: -2,
        DOCID: this.inputDocument.DOCID,
        DOCTYPE: this.inputDocument.DOCTYPE,
        PRODID: null,
        DOCITEMPRODNAME: '',
        DOCITEMPRICE: 0,
        DOCITEMQTY: 1,
        DOCITEMVPRICE: 0,
        DOCITEMCURRID: this.inputDocument.DOCCURRID,
        DOCITEMCRATE: this.inputDocument.DOCRATEVAL,
        PAYITEMCLIID: this.sDI.PAYITEMCLIID - 1,
        PAYITEMDEPID: null,
        DOCITEMTEXT1: '',
        DOCITEMTEXT2: 'Нет',
        CURCODE: '',
        U4ETKA: null,
        PRODVAT: this.globalSettinsData.vatValue,
        cloneIndex: 0
      };

      // Insert newDi after selectedIndex
      this.docItems = [
        ...this.docItems.slice(0, selectedIndex),
        newDi,
        ...this.docItems.slice(selectedIndex)
      ];

      let ppe: DocItems = this.docItems.find(e => e.DOCITEMID == -2);
      this.onDiRowEditInit(ppe);
      this.docItemTable.initRowEdit(ppe);
    }
  }

  onAddRowBelow(event) {
    console.log('*** EditDocumentContentComponent in onAddRowBelow: ' + JSON.stringify(event));

    // Insert newDi into docItems right after the selected sDI (i.e., after its index)
    if (this.sDI) {
      const selectedIndex = this.docItems.findIndex(item => item.DOCITEMID === this.sDI.DOCITEMID);

      let newDi: DocItems = {
        DOCITEMID: -2,
        DOCID: this.inputDocument.DOCID,
        DOCTYPE: this.inputDocument.DOCTYPE,
        PRODID: null,
        DOCITEMPRODNAME: '',
        DOCITEMPRICE: 0,
        DOCITEMQTY: 1,
        DOCITEMVPRICE: 0,
        DOCITEMCURRID: this.inputDocument.DOCCURRID,
        DOCITEMCRATE: this.inputDocument.DOCRATEVAL,
        PAYITEMCLIID: this.sDI.PAYITEMCLIID + 1,
        PAYITEMDEPID: null,
        DOCITEMTEXT1: '',
        DOCITEMTEXT2: 'Нет',
        CURCODE: '',
        U4ETKA: null,
        PRODVAT: this.globalSettinsData.vatValue,
        cloneIndex: 0
      };

      // Insert newDi after selectedIndex
      this.docItems = [
        ...this.docItems.slice(0, selectedIndex + 1),
        newDi,
        ...this.docItems.slice(selectedIndex + 1)
      ];

      let ppe: DocItems = this.docItems.find(e => e.DOCITEMID == -2);
      this.onDiRowEditInit(ppe);
      this.docItemTable.initRowEdit(ppe);
    }
  }

  /**
   * Called by PrimeNG's table when row editing begins.
   *
   * Assigns a unique `cloneIndex` to `esg` and pushes a shallow copy into
   * {@link docItemsClone} so the row can be restored if the user cancels.
   * Also fires {@link backgroundRefreshStockForRow} to silently update stock
   * and shipped quantities without blocking the UI.
   *
   * @param esg - The document item row entering edit mode.
   */
  onDiRowEditInit(esg: DocItems) {
    console.log('*** EditDocumentContentComponent in onPpRowEditInit: entry');
    //environment.production || console.table(esg);

    let newCloneIndex: number = 0;

    /*
     * Quite tricky algoritm:
     * Because we can edit simultaneously more than one row in the table.
     * Therefore for every edited row we have to create cloned copy in shadow array [docItemsClone]
     * We can't predict the order of starting and ending the edit process
     * 
     * So - Search for max cloneIndex in current table and assign +1 to newly created row copy
     *
     * When row either cancelled or saved - clone index resetted to 0, clone copy deleted from shadow array
     *
     */
    this.docItems.forEach(pp => { newCloneIndex = Math.max(newCloneIndex, pp.cloneIndex) });

    console.log('*** EditDocumentContentComponent in onPpRowEditInit: newCloneIndex is: ' + newCloneIndex);

    esg.cloneIndex = ++newCloneIndex;
    this.docItemsClone.push({ ...esg });

    esg.DOCITEMCURRID = this.inputDocument.DOCCURRID;
    esg.DOCITEMCRATE = this.inputDocument.DOCRATEVAL;

    this.editMode = true;
    this.editing.emit(this.editMode);

    this.backgroundRefreshStockForRow(esg);
  }

  /**
   * Silently refreshes stock and shipped-quantity data for the row currently
   * being edited, without showing the global loading spinner.
   *
   * Uses the batch endpoint `getDocItemsStockAndShipped` and applies the result
   * only to the specific `di` instance already in memory.  Errors are swallowed
   * (returns {@link EMPTY}) so editing is never blocked by a background refresh failure.
   *
   * @param di - The document item row whose stock / shipped data should be refreshed.
   */
  private backgroundRefreshStockForRow(di: DocItems): void {
    // Fire-and-forget: refresh stock/shipped in the row's prodInfo from the batch endpoint.
    // Does not set contentIsLoading — purely background, non-blocking.
    const sub = this.docApi.getDocItemsStockAndShipped(
      this.inputDocument.DOCID,
      this.inputDocument.DOCMANCLIID,
      this.ms, this.is, this.as,
      new Date()
    ).pipe(
      catchError(() => EMPTY),
      map(summaries => summaries.find(s => s.DOCITEMID === di.DOCITEMID)),
      filter(summary => !!summary)
    ).subscribe(summary => {
      if (di.prodInfo && summary) {
        di.prodInfo.PRODSTOCK = summary.PRODSTOCK;
        di.DOCITEMSHIPPED = summary.DOCITEMSHIPPED;
        di.DOCITEMGLOBALSTOCK = summary.PRODSTOCK;
        di.mst = (di.DOCITEMQTY - di.DOCITEMSHIPPED) > di.prodInfo.PRODSTOCK;
      }
    });

    this.addSubscription(sub);
  }

  /**
   * Called by PrimeNG's table when the user cancels an in-progress row edit.
   *
   * - For **new rows** (`DOCITEMID === -2`): removes the row from {@link docItems}.
   * - For **existing rows**: restores the row from {@link docItemsClone} and calls
   *   {@link fastReloadDocContent} to re-sync with the server.
   *
   * Cleans up the clone entry, recalculates document summary, and clears `editMode`.
   *
   * @param esg   - The document item row whose edit is being cancelled.
   * @param index - The row's current position in the rendered table.
   */
  onDiRowEditCancel(esg: DocItems, index: number) {
    console.log('*** EditDocumentContentComponent in onPpRowEditCancel: ' + index);
    environment.production || console.log('*** EditDocumentContentComponent in onPpRowEditCancel: ' + JSON.stringify(esg));

    //environment.production || console.table(esg);

    this.contentIsLoading = true;
    this.contentLoading.emit(this.contentIsLoading);

    const sub = of(0).pipe(
      map(() => {
        // environment.production || console.table(this.docItemsClone);
        let ppid = this.docItemsClone.findIndex(sg => sg.cloneIndex == esg.cloneIndex);
        // environment.production || console.log('*** EditDocumentContentComponent in onPpRowEditCancel: this.docItemsClone['+ ppid +']' + JSON.stringify(this.docItemsClone[ppid]));

        if (esg.DOCITEMID == -2) {
          // Cancel the newly created record
          // simply delete

          this.docItems.splice(index, 1);

        } else {
          // Cancel editing of existing record
          // have to reload it from database

          // environment.production || console.log('*** EditDocumentContentComponent in onPpRowEditCancel: this.docItems[index]: ' + JSON.stringify(this.docItems[index]));

          this.docItems[index] = { ...this.docItemsClone[ppid] };

          this.docItems[index].cloneIndex = 0;
          this.fastReloadDocContent(this.docItems[index]);
        }
        return ppid;
      }),

      tap(ppid => {
        this.docItemsClone.splice(ppid, 1);
      }),

      concatMap(() => this.calculateDocumentSummaryObservable()),

      catchError(error => {
        environment.production || console.error('EditDocumentContentComponent onDiRowEditCancel error:', error);
        return EMPTY;
      }),

      finalize(() => {
        this.contentIsLoading = false;
        this.contentLoading.emit(this.contentIsLoading);
        this.docItemTable.cancelRowEdit(esg);
        this.editMode = false;
        this.editing.emit(this.editMode);
      }),

    ).subscribe();

    this.addSubscription(sub);

    //  console.log('*** EditDocumentContentComponent in onPpRowEditSave: prodParts');
    //  environment.production || console.table(this.prodParts);
    //  console.log('*** EditDocumentContentComponent in onPpRowEditSave: prodPartsClone ');
    //  environment.production || console.table(this.prodPartsClone);
  }

  onDOCITEMVPRICEChange(event, di: DocItems) {
    this.recalculatePrices(di, ['vprice']);
  }

  onDOCITEMQTYChange(event, di: DocItems) {
    this.qtyChange$.next(di);
  }

  onDOCITEMPRICEWOVATChange(event, di: DocItems) {
    environment.production || console.log('*** EditDocumentContentComponent in onDOCITEMPRICEWOVATChange: ' + JSON.stringify(event));
    environment.production || console.log('*** EditDocumentContentComponent in onDOCITEMPRICEWOVATChange: ' + JSON.stringify(di));

    this.recalculatePrices(di, ['priceNoVat']);
  }

  onDOCITEMPRICEChange(event, di: DocItems) {
    //console.log('*** EditDocumentContentComponent in onDOCITEMPRICEChange: ' + JSON.stringify(event));
    environment.production || console.log('*** EditDocumentContentComponent in onDOCITEMPRICEChange: ' + JSON.stringify(di));

    this.recalculatePrices(di, ['price']);
  }

  onDISCOUNTChange(event, di: DocItems) {
    environment.production || console.log('*** EditDocumentContentComponent in onDISCOUNTChange: ' + JSON.stringify(event));
    environment.production || console.log('*** EditDocumentContentComponent in onDISCOUNTChange: ' + JSON.stringify(di));

    this.recalculatePrices(di, ['discount']);
  }

  onPAYITEMCLIIDChange(event, di: DocItems) {
    environment.production || console.log('*** EditDocumentContentComponent in onPAYITEMCLIIDChange: ' + JSON.stringify(event));
    environment.production || console.log('*** EditDocumentContentComponent in onPAYITEMCLIIDChange: ' + JSON.stringify(di));

    this.docItems = [... this.docItems.sort((a, b) => (a.PAYITEMCLIID - b.PAYITEMCLIID))];
  }

  setDiscount(event, di: DocItems) {
    environment.production || console.log('*** EditDocumentContentComponent in setDiscount: ' + JSON.stringify(event));

    di.DISCOUNT = event.value;

    this.recalculatePrices(di, ['discount']);
  }

  /** Short label for `customerTier` -- `null` for every tier except the two that
   *  actually get elevated pricing, same scoping `companyselector`'s own badge uses. */
  private get tierLabel(): string | null {
    switch (this.customerTier) {
      case 'key_partner': return 'Ключовий партнер';
      case 'key_account': return 'Ключовий клієнт';
      default: return null;
    }
  }

  /**
   * Tooltip for the "Скидка" dropdown (2026-09-21). When the row's current `DISCOUNT`
   * still equals the customer's tier-entitled percentage -- true right after
   * `onProductSelection()` sets it, false the moment a manager hand-edits it -- names
   * the tier so it's visible *why* this particular discount is showing, not just that
   * one is. Falls back to the original generic hint otherwise (no tier, untiered
   * customer, or a discount the manager has since overridden by hand).
   */
  discountTierTooltip(di: DocItems): string {
    const genericHint = 'Выбрать скидку на товар';
    if (!di.prodInfo || !this.tierLabel) {
      return genericHint;
    }

    const tierPercent = getTierDiscountPercent(this.customerTier, di.prodInfo);
    if (tierPercent <= 0 || round2(di.DISCOUNT) !== round2(tierPercent)) {
      return genericHint;
    }

    return `Знижка застосована згідно рівня клієнта: ${this.tierLabel} (${formatNumber(tierPercent, 'uk', '1.0-1')}%)`;
  }

  /**
   * Tooltip for the bulk "Скидка" split-button's default click (2026-09-21) -- that
   * click now applies `customerTier`'s percentage document-wide (see `applySetDiscount()`'s
   * `'client'` case), so the tooltip names which tier/percentage a click is about to
   * apply, rather than the original generic hint alone.
   */
  get bulkDiscountTierTooltip(): string {
    const base = 'Проставить скидку по всему документу';
    return this.tierLabel ? `${base} (рівень клієнта: ${this.tierLabel})` : base;
  }

  /**
   * Visible label for the bulk "Скидка" split-button (2026-09-21, owner: a tooltip
   * alone isn't enough -- "it is not clear which one" without hovering first). Names
   * the tier the same way the per-row dropdown's own options are labeled, so a manager
   * can tell at a glance what a click will do instead of discovering it after the
   * fact. No single percentage can go here the way a per-row option shows one --
   * `PRODBASEDISC`/`PRODQTYDISC`/`PRODDIALDISC` are set per *product*, so the same
   * tier can mean a different percentage on every row in the same document (see
   * `PROMPT_customer-level-discount-auto-set.md`'s "Concept map"); the tier name is
   * the one thing that *is* constant across the whole click.
   */
  get bulkDiscountLabel(): string {
    return this.tierLabel ? `Скидка: ${this.tierLabel}` : 'Скидка';
  }

  checkDiState(event, di: DocItems) {
    environment.production || console.log('*** EditDocumentContentComponent in checkDiState: ' + JSON.stringify(event));
    environment.production || console.log('*** EditDocumentContentComponent in checkDiState: ' + JSON.stringify(di));
    if (di.hlt) {

      // di.hlt = (di.bip || di.bmd || di.cnm || di.rnm || di.mtc || di.lpf || di.cpn || di.nta);

      this.messageService.add({
        severity: 'warn',
        summary: di.PRODCODE + ' информация: ',
        life: 10000,
        detail: ''
          + (di.bip ? 'Цена ниже входной! ' : '')
          + (di.bmd ? 'Скидка больше максимальной! ' : '')
          + (di.cnm ? 'Валюта не соответствует установленной в документе! ' : '')
          + (di.rnm ? 'Курс не соответствует установленному в документе! ' : '')
          + (di.cpn ? 'Валютная цена не соответствует расчетной! ' : '')
          + (di.mtc ? 'Не указан УКТЗЕД товара! ' : '')
          + (di.nta ? 'Не подтвержден УКТЗЕД товара! ' : '')
          + (di.lpf ? 'Возможный убыток от операции! ' : '')
          + (di.npc ? 'Не установлена категория товара ' : '')
      });
    } else {
      this.messageService.add({ severity: 'success', summary: di.PRODCODE + ' информация: OK' });
    }
  }

  // Bulk discount application
  // Helper to apply discount to all items

  applySetDiscount(dt: string) {
    console.log('*** EditDocumentContentComponent in applySetDiscount: ' + dt);
    this.docItems.forEach((di) => {
      switch (this.inputDocument.DOCTYPE) {
        case 1:
        case 12:
          di.DOCITEMCURRID = this.inputDocument.DOCCURRID;
          // Ensure backup exists before API calls
          if (!di.DOCINCOMEPRICE && di.prodInfo?.PRODINPRICE) {
            di.DOCINCOMEPRICE = di.prodInfo.PRODINPRICE * this.inputDocument.DOCRATEVAL;
          }
          di.DOCITEMCRATE = this.inputDocument.DOCRATEVAL;

          switch (dt) {

            case 'min': di.DISCOUNT = di.prodInfo.PRODBASEDISC; this.onDISCOUNTChange(null, di); break;
            case 'mid': di.DISCOUNT = di.prodInfo.PRODQTYDISC; this.onDISCOUNTChange(null, di); break;
            case 'max': di.DISCOUNT = di.prodInfo.PRODDIALDISC; this.onDISCOUNTChange(null, di); break;
            case 'non': di.DISCOUNT = 0; this.onDISCOUNTChange(null, di); break;
            // The split-button's own default click (doSetDiscount('client')) has passed
            // 'client' here since this menu existed, but no case ever matched it -- every
            // click silently did nothing to DISCOUNT while still showing the "Готово!"
            // success toast (found 2026-09-21, owner: the button doesn't reflect customer
            // pricing). Now applies the customer's tier-entitled percentage per row, the
            // same source `onProductSelection()`'s per-row default already uses -- an
            // untiered/no-tier customer resolves to 0%, same as 'non'.
            case 'client': di.DISCOUNT = getTierDiscountPercent(this.customerTier, di.prodInfo); this.onDISCOUNTChange(null, di); break;
          }
          break;
      }
    });
  }

  // Bulk discount application entry point
  // Calls applySetDiscount inside Observable pipeline to ensure proper sequencing

  doSetDiscount(dt: string) {

    this.addSubscription(
      of(0).pipe(
        map(() => {
          this.applySetDiscount(dt);
        }),
        concatMap(() => this.calculateDocumentSummaryObservable()),
        finalize(() => {
          this.messageService.add({
            severity: 'success',
            summary: ' Готово! Не забудьте сохранить результат после проверки.'
          });
          this.allDocItemsChanged = true;
          this.editing.emit(this.allDocItemsChanged);
        })
      ).subscribe()
    );
  }

  // U4ETKA price refresh procedure
  // Recalculates U4ETKA prices based on current document sums and payments
  // Only allowed if document fully paid and U4ETKA margin enough to cover currency rate change

  updU4etkaRefresh(event) {
    console.log('*** EditDocumentContentComponent in updU4etkaRefresh: ' + JSON.stringify(event));

    let docSum = this.inputDocument.DOCSUM;             // Document sum in main currency
    let paySum = this.inputDocument.DOCPAIDSUM;         // Document paid sum in main currency
    let docSumV = this.inputDocument.DOCVSUM;           // Document sum in document currency
    let paySumV = this.inputDocument.DOCPAIDSUMVAL;     // Document paid sum in document currency
    let u4eSum = this.inputDocument.DOCSUMU4ETKA;       // Document U4ETKA sum in main currency

    console.log('*** EditDocumentContentComponent in updU4etkaRefresh: ' + JSON.stringify({ DS: docSum, DV: docSumV, PS: paySum, PV: paySumV, US: u4eSum }));

    if (round2(paySum) >= round2(docSum)) {

      // Main process allowed and here

      let u4c = docSum / u4eSum;    // U4ETKA coefficient
      let p4c = docSumV / paySumV;  // main correction coefficient (currency rate adjustment)

      console.log('*** EditDocumentContentComponent in updU4etkaRefresh: ' + JSON.stringify({ UC: u4c, PC: p4c }));

      if (u4c >= p4c) {

        this.contentLoading.emit(this.contentIsLoading = true);

        this.diGroupQuery =
          this.reassignSubscription(
            this.diGroupQuery,

            from(this.docItems).pipe(
              map(di => {
                // Only adjust U4ETKA by the currency rate change coefficient
                di.U4ETKA = di.U4ETKA * p4c;

                // Recalculate only the fast summaries (U4ETKA * qty)
                // This does NOT touch prices, vprice, or discount
                di.DOCITEMSUMU4 = di.DOCITEMQTY * di.U4ETKA;

                // Update profit calculations based on new U4ETKA
                // (calculateRowProfit does not modify prices or discounts)
                // this.calculateRowProfit(di);

                // Profit
                di.DOCITEMPROFIT = (di.U4ETKA || di.DOCITEMPRICE) - (di.DOCINCOMEPRICE ?? 0);
                di.DOCITEMPROFITSUM = di.DOCITEMQTY * di.DOCITEMPROFIT;

                return di;
              }),
              // concatMap(di => this.calculateDiRowProfitObservable(di)),       // <= need to check again wether this procedure needed here
              concatMap(di => this.calculateDiRowStatusObservable(di)),

              toArray(),
              concatMap(() => this.calculateDocumentSummaryObservable()),

              finalize(() => {
                // Recalculate document-level summaries
                // this.calculateDocumentSummary();

                this.messageService.add({
                  severity: 'success',
                  summary: ' Готово! Не забудьте сохранить результат после проверки.'
                });

                this.allDocItemsChanged = true;
                this.editing.emit(this.allDocItemsChanged);

                this.contentLoading.emit(this.contentIsLoading = false);
              })
            ).subscribe()
          );

      } else {

        // Can't do anything because margin in U4ETKA not enough to cover price change

        this.messageService.add({
          severity: 'error',
          summary: ' Невозможно сделать пересчет. Запаса по учетке не достаточно!'
        });
      }

    } else {

      // Can't do anything due to lack of payments

      this.messageService.add({
        severity: 'error',
        summary: ' Невозможно сделать пересчет. Счет не оплачен полностью!'
      });
    }
  }

  updCurrPrice(event) {
    console.log('*** EditDocumentContentComponent in updCurrPrice: ' + JSON.stringify(event));

    this.diGroupQuery =
      this.reassignSubscription(
        this.diGroupQuery,
        from(this.docItems).pipe(
          map(e => {
            e.DOCITEMCURRID = this.inputDocument.DOCCURRID;
            e.DOCITEMCRATE = this.inputDocument.DOCRATEVAL;
            e.DOCITEMVPRICE = e.DOCITEMPRICE / e.DOCITEMCRATE;
            return e;
          }),
          concatMap(e => this.calculateDiRowStatusObservable(e)),
          toArray(),
          concatMap(() => this.calculateDocumentSummaryObservable()),

          finalize(() => {
            this.allDocItemsChanged = true;
            this.editing.emit(this.allDocItemsChanged);
          }))
          .subscribe()
      );
  }

  updMainPrice(event) {
    console.log('*** EditDocumentContentComponent in updMainPrice: ' + JSON.stringify(event));

    this.diGroupQuery =
      this.reassignSubscription(
        this.diGroupQuery,

        from(this.docItems)
          .pipe(
            map(e => {
              e.DOCITEMCURRID = this.inputDocument.DOCCURRID;
              e.DOCITEMCRATE = this.inputDocument.DOCRATEVAL;
              e.DOCITEMPRICE = round2(e.DOCITEMVPRICE * e.DOCITEMCRATE);
              return e;
            }),
            concatMap(e => this.calculateDiRowStatusObservable(e)),
            toArray(),
            concatMap(() => this.calculateDocumentSummaryObservable()),

            finalize(() => {
              this.allDocItemsChanged = true;
              this.editing.emit(this.allDocItemsChanged);
            }))
          .subscribe()
      );
  }

  onSaveRows(event) {
    console.log('*** EditDocumentContentComponent in onSaveRows: ' + JSON.stringify(event));

    this.diSaveQuery =

      this.reassignSubscription(
        this.diSaveQuery,

        from(this.docItems).pipe(
          tap(() => this.contentLoading.emit(this.contentIsLoading = true)),

          map(di => {

            di.DOCITEMCURRID = this.inputDocument.DOCCURRID;
            di.DOCITEMCRATE = this.inputDocument.DOCRATEVAL;
            di.DOCITEMVPRICE = di.DOCITEMPRICE / di.DOCITEMCRATE;

            return di;
          }),

          concatMap(di => this.docApi.putDocItemById(di).pipe(
            concatMap(e => this.calculateDiRowStatusObservable(di)),
          )),
          toArray(),

          finalize(() => {
            this.reloadDocContent(null);
            // this.calculateDocItemsFields(this.docItems);   we need it somewhere in async flow?
            this.editing.emit(this.allDocItemsChanged = false);

            this.contentLoading.emit(this.contentIsLoading = false);
          })).subscribe()
      );
  }

  onUndoRows(event) {
    console.log('*** EditDocumentContentComponent in onUndoRows: ' + JSON.stringify(event));

    this.restoreDiCopy();
    // this.calculateDocItemsFields(this.docItems);   we need it somewhere in async flow?
    this.editing.emit(this.allDocItemsChanged = false);
  }

  onRemoveAllRow(event) {
    console.log('*** EditDocumentContentComponent in onRemoveRow: ' + JSON.stringify(event));

    this.deleteAllRows = true;
    this.confirmText = 'DELETE ' + this.docItems.length;
    this.typeDeleteConfirmation = true;
    this.confirmDeleteOn = true;
  }

  onRemoveRow(event) {
    console.log('*** EditDocumentContentComponent in onRemoveRow: ' + JSON.stringify(event));
    this.deleteAllRows = false;
    this.typeDeleteConfirmation = false;
    this.confirmDeleteOn = true;
  }

  onDeleteConfirmation(event) {
    console.log('*** EditDocumentContentComponent onDeleteConfirmation: ' + JSON.stringify(event));
    this.confirmDeleteOn = false;

    if (event) {
      console.log('*** EditDocumentContentComponent onDeleteConfirmation DO DELETE!!! NOW!!!');

      if (this.deleteAllRows) {
        console.log('*** EditDocumentContentComponent onDeleteConfirmation DO DELETE ALL !!! NOW!!! ********** ALL ********');
        const sub = this.docApi.deleteAllDocItemByDocId(this.inputDocument.DOCID)
          .subscribe(() => {
            console.log('*** EditDocumentContentComponent onDeleteAllItemsConfirmation: DONE!');
            this.sDI = null;
            this.reloadDocContent(null);
          });

        this.activeSubscriptions.push(sub);

      } else {
        if (this.sDI) {

          const sub = this.docApi.deleteDocItemById(this.sDI.DOCITEMID).pipe(
            tap(() => {
              console.log('*** EditDocumentContentComponent onDeleteConfirmation: DONE!');
              // this.reloadDocContent(null);

              let idx = this.docItems.findIndex(d => d.DOCITEMID == this.sDI.DOCITEMID);
              if (idx >= 0) {
                this.docItems.splice(idx, 1);
                this.docItems = [...this.docItems];
              }
              this.sDI = null;
            }),

            catchError(error => {
              environment.production || console.error('EditDocumentContentComponent onDeleteConfirmation error:', error);
              return EMPTY;
            }),

            concatMap(() => this.calculateDocumentSummaryObservable()),

          ).subscribe();

          this.activeSubscriptions.push(sub);
        }
      }
    }
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    import("file-saver").then(FileSaver => {
      let EXCEL_TYPE =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      let EXCEL_EXTENSION = ".xlsx";
      const data: Blob = new Blob([buffer], {
        type: EXCEL_TYPE
      });

      saveAs(
        data,
        fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION
      );
    });
  }

  /*
  DOCID  DOCTYPEID  DOCDATE  DOCEXPDATE  DOCVALDATE  DOCCLIENTSID  DOCPERSONID
  DOCMANCLIID  DOCMANPERID  DOCNUMBER  DOCNOTE1  DOCNOTE2  PRIMDOCID  DOCCLIDEPID
  DOCMANDEPID  DOCCURRID  DOCRATEVAL  DOCACTIVE  DOCFLAG  CLIENTSNAME  DOCLCOCLIID
  LOCALCOMP  PERSONNAME  DOCNAME  DOCSUM  PAYDSUM  SHIPSUM  AUTHOR  CURSTR  LOCALCOMPID
  PAIDJUSTNOW  PAIDNOWDATE  DOCPCOCLIID  PARTNERNAME  PARTNERNAMESTR
  fs	ps	fp	pp	np	go	td	fd
  */

  onExportData() {
    console.log('*** EditDocumentContentComponent onExportData: ')

    import("xlsx").then(xlsx => {

      environment.production || console.table(this.docItems);

      let exportData: any =
        this.docItems
          .filter(e => this.countEmpty || (e.DOCITEMQTY && (e.DOCITEMQTY != 0)))
          .map(e => ({

            'Код товара': e.PRODCODE,
            'Штрихкод (EAN)': e.PRODBARCODEMASK,
            'Производитель': e.PRODOEMNAME,
            'Поставщик': e.prodInfo?.VENDORNAME,   // default supplier (ProdVendors), not the manufacturer
            'Наименование товара': e.DOCITEMPRODNAME,
            'Количество': e.DOCITEMQTY,
            'Цена с НДС': e.DOCITEMPRICE,
            'Сумма с НДС': e.DOCITEMSUM,
            'Цена в валюте': e.DOCITEMVPRICE,
            'Сумма в валюте': e.DOCITEMVSUM,
            'Цена без НДС': e.DOCITEMPRICEWOVAT,
            'Сумма без НДС': e.DOCITEMSUMWOVAT,
            'Ставка НДС': e.PRODVAT,
            'Таможенный код': e.PRODCUSTCODE,
            'Текст 1': e.DOCITEMTEXT1,
            'Текст 2': e.DOCITEMTEXT2,
            'Ссылка на товар': e.PRODWWWPAGE,
            'Учетка': e.U4ETKA,
            'Сумма учетки': e.DOCITEMSUMU4,
            'Отгружено': e.DOCITEMSHIPPED,

            'Валюта': e.CURCODE,
            'Курс': e.DOCITEMCRATE,

            'Розница': e.DOCITEMRETPRICE,
            'Розница в валюте': e.DOCITEMRETVPRICE,
            'Скидка': e.DISCOUNT,

          }));

      const worksheet = xlsx.utils.json_to_sheet(exportData || []);
      const workbook = { Sheets: { document: worksheet }, SheetNames: ['document'] };

      const excelBuffer: any = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array"
      });

      this.saveAsExcelFile(excelBuffer, "document");
    });
  }

  onExportDataWithProfit() {
    console.log('*** EditDocumentContentComponent onExportData: ')

    import("xlsx").then(xlsx => {

      environment.production || console.table(this.docItems);

      let exportData: any =
        this.docItems
          .filter(e => this.countEmpty || (e.DOCITEMQTY && (e.DOCITEMQTY != 0)))
          .map(e => ({

            'Код товара': e.PRODCODE,
            'Штрихкод (EAN)': e.PRODBARCODEMASK,
            'Производитель': e.PRODOEMNAME,
            'Поставщик': e.prodInfo?.VENDORNAME,   // default supplier (ProdVendors), not the manufacturer
            'Наименование товара': e.DOCITEMPRODNAME,
            'Количество': e.DOCITEMQTY,
            'Цена с НДС': e.DOCITEMPRICE,
            'Сумма с НДС': e.DOCITEMSUM,
            'Цена в валюте': e.DOCITEMVPRICE,
            'Сумма в валюте': e.DOCITEMVSUM,
            'Цена без НДС': e.DOCITEMPRICEWOVAT,
            'Сумма без НДС': e.DOCITEMSUMWOVAT,
            'Ставка НДС': e.PRODVAT,
            'Таможенный код': e.PRODCUSTCODE,
            'Текст 1': e.DOCITEMTEXT1,
            'Текст 2': e.DOCITEMTEXT2,
            'Ссылка на товар': e.PRODWWWPAGE,
            'Учетка': e.U4ETKA,
            'Сумма учетки': e.DOCITEMSUMU4,
            'Отгружено': e.DOCITEMSHIPPED,

            'На складе': e.prodInfo.PRODSTOCK,
            'В резерве': e.prodInfo.PRODRESERVE,
            'Продано': e.prodInfo.PRODSOLD,
            'В пути': e.prodInfo.PRODONWAY,

            'Валюта': e.CURCODE,
            'Курс': e.DOCITEMCRATE,

            'Розница': e.DOCITEMRETPRICE,
            'Розница в валюте': e.DOCITEMRETVPRICE,
            'Скидка': e.DISCOUNT,

            'Входная цена': e.DOCINCOMEPRICE,
            'Доход': e.DOCITEMPROFIT,
            'Сумма дохода': e.DOCITEMPROFITSUM,

          }));

      const worksheet = xlsx.utils.json_to_sheet(exportData || []);
      const workbook = { Sheets: { document: worksheet }, SheetNames: ['document'] };

      const excelBuffer: any = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array"
      });

      this.saveAsExcelFile(excelBuffer, "document");
    });
  }

  onExportDataWithLogistic() {
    console.log('*** EditDocumentContentComponent onExportData: ')

    import("xlsx").then(xlsx => {

      environment.production || console.table(this.docItems);

      let exportData: any =
        this.docItems
          .filter(e => this.countEmpty || (e.DOCITEMQTY && (e.DOCITEMQTY != 0)))
          .map(e => ({

            'Код товара': e.PRODCODE,
            'Штрихкод (EAN)': e.PRODBARCODEMASK,
            'Производитель': e.PRODOEMNAME,
            'Поставщик': e.prodInfo?.VENDORNAME,   // default supplier (ProdVendors), not the manufacturer
            'Наименование товара': e.DOCITEMPRODNAME,
            'Количество': e.DOCITEMQTY,
            'Цена с НДС': e.DOCITEMPRICE,
            'Сумма с НДС': e.DOCITEMSUM,
            'Цена в валюте': e.DOCITEMVPRICE,
            'Сумма в валюте': e.DOCITEMVSUM,
            'Цена без НДС': e.DOCITEMPRICEWOVAT,
            'Сумма без НДС': e.DOCITEMSUMWOVAT,
            'Ставка НДС': e.PRODVAT,
            'Таможенный код': e.PRODCUSTCODE,
            'Текст 1': e.DOCITEMTEXT1,
            'Текст 2': e.DOCITEMTEXT2,
            'Ссылка на товар': e.PRODWWWPAGE,
            'Учетка': e.U4ETKA,
            'Сумма учетки': e.DOCITEMSUMU4,
            'Отгружено': e.DOCITEMSHIPPED,

            'На складе': e.prodInfo.PRODSTOCK,
            'В резерве': e.prodInfo.PRODRESERVE,
            'Продано': e.prodInfo.PRODSOLD,
            'В пути': e.prodInfo.PRODONWAY,

            'Валюта': e.CURCODE,
            'Курс': e.DOCITEMCRATE,

            'Розница': e.DOCITEMRETPRICE,
            'Розница в валюте': e.DOCITEMRETVPRICE,
            'Скидка': e.DISCOUNT,

            'Цена поставщика': e.prodInfo.VENDORPRICE,
            'Вес': e.prodInfo.PRODWEIGHT,
            'Доставка': e.prodInfo.VENADDCONST,
            'Пошлина': e.prodInfo.VENMULCONST,
            'Дополнительно +': e.prodInfo.VENADDVAR,
            'Дополнительно *': e.prodInfo.VENMULVAR,

            'Входная цена': e.DOCINCOMEPRICE,
            'Доход': e.DOCITEMPROFIT,
            'Сумма дохода': e.DOCITEMPROFITSUM,

          }));

      const worksheet = xlsx.utils.json_to_sheet(exportData || []);
      const workbook = { Sheets: { document: worksheet }, SheetNames: ['document'] };

      const excelBuffer: any = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array"
      });

      this.saveAsExcelFile(excelBuffer, "document");
    });
  }

  onStateSave(state) {
    console.log('*** EditDocumentContentComponent onStateSave: ' + JSON.stringify(state));
  }

  onStateRestore(state) {
    console.log('*** EditDocumentContentComponent onStateRestore: ' + JSON.stringify(state));
    if (state.filters) {
      for (let f in state.filters) {
        let fltItem = state.filters[f];
        this.cf3[f] = Array.isArray(fltItem) ? fltItem[0].value : fltItem.value;
      }
    }
  }

  cleanSortMode(event) {
    console.log('*** EditDocumentContentComponent cleanSortMode: ' + JSON.stringify(event));

    this.docItemTable.reset();
    this.reloadDocContent(event);
  }

  updateRowIndexes(event) {
    console.log('*** EditDocumentContentComponent updateRowIndexes: ' + JSON.stringify(event));

    let rowIndex = 10;

    this.diSaveQuery =
      from(this.docItems)
        .pipe(
          tap(() => {
            this.contentIsLoading = true;
            this.contentLoading.emit(this.contentIsLoading);
          }),

          concatMap(di => {
            di.PAYITEMCLIID = rowIndex;
            rowIndex += 10;

            return this.docApi.putDocItemById(di);
          }),

          finalize(() => {
            this.contentIsLoading = false;
            this.contentLoading.emit(this.contentIsLoading);
            this.reloadDocContent(event);
          })
        )
        .subscribe();
  }

  clearU4etkaValue(event) {
    console.log('*** EditDocumentContentComponent clearU4etkaValue: ' + JSON.stringify(event));

    this.diSaveQuery =
      from(this.docItems)
        .pipe(
          tap(() => {
            this.contentIsLoading = true;
            this.contentLoading.emit(this.contentIsLoading);
          }),

          concatMap(di => this.docApi.putDocItemById({ ...di, U4ETKA: null })),

          finalize(() => {
            this.contentIsLoading = false;
            this.contentLoading.emit(this.contentIsLoading);
            this.reloadDocContent(event);
          })
        )
        .subscribe();
  }

  profitModeChange(event) {
    console.log('*** EditDocumentContentComponent profitModeChange: ' + JSON.stringify(event));

    if (this.inputDocument) {
      this.docItemList$.next(this.inputDocument.DOCID);
    }
  }

  countEmptyModeChange(event) {
    console.log('*** EditDocumentContentComponent countEmptyModeChange: ' + JSON.stringify(event));

    this.countEmpty = event.checked;

    if (this.inputDocument) {
      this.docItemList$.next(this.inputDocument.DOCID);
      this.countEmptyChange.emit(this.countEmpty);
    }
  }

  /**
   * Whether a row is rendered in the items table.
   *
   * When {@link countEmpty} is off, zero-quantity rows are hidden — the same set of rows
   * that is excluded from the totals, export and print. A row currently being edited stays
   * visible even if its quantity is (or becomes) zero, so it does not vanish under the user.
   * Only the rendering is affected: {@link docItems} still holds every row.
   *
   * @param di - The document item row.
   * @param editing - Whether PrimeNG reports the row as being edited.
   */
  isDocItemRowVisible(di: DocItems, editing: boolean): boolean {
    return this.countEmpty || editing || (di.DOCITEMQTY != null && di.DOCITEMQTY !== 0);
  }
}

